import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import LandingPage from './components/landing/LandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import FAQPage from './components/FAQPage';
import Dashboard from './components/Dashboard';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import HelpCenterPage from './pages/HelpCenterPage';
import EventInputScreen from './components/EventInputScreen';
import LoadingScreen from './components/LoadingScreen';
import ThemeCarousel from './components/ThemeCarousel';
import Plan from './components/Plan';
import ThemeDetailsLoading from './components/ThemeDetailsLoading';
import ThemeSelectionModal from './components/ThemeSelectionModal';
import ThemeEditModal from './components/ThemeEditModal';
import SavePlanModal from './components/SavePlanModal';
import ShareModal from './components/ShareModal';
import ConfirmationModal from './components/ConfirmationModal';
import AuthModal from './components/landing/AuthModal';
import PublicGuestShowcase from './components/PublicGuestShowcase';
import OccasionLandingPage from './pages/OccasionLandingPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { trackEvent } from './utils/telemetry';
import { checkUnsavedChanges, clearUnsavedChanges, markUnsavedChanges } from './utils/unsavedChanges';
import * as planUtils from './utils/plans';
import * as authUtils from './utils/auth';
import {
  getInFlightThemeJob,
  getInFlightPlanJob,
  clearInFlightThemeJob,
  clearInFlightPlanJob
} from './utils/asyncJobs';
import { BACKEND_URL } from './utils/backendUrl';
import { initializeItemsFromTheme } from './utils/shoppingListUtils';
import { getThemePlanKey, themesMatch } from './utils/themePlanKeys';
import { withPlanEstimateCoreSnapshot, ensurePlanEstimateCore } from './utils/planEstimateCore';
import {
  clearNavSnapshot,
  getInitialAppRouteState,
  writeNavSnapshot
} from './utils/navSnapshot';
import {
  pushAppHistory,
  readHistoryRoute,
  replaceAppHistoryRoute
} from './utils/appHistory';
import { getPlanningFlow } from './utils/planningFlow';
import './index.css';
import TopStepperNav from './components/TopStepperNav';
import AppLayout from './components/layout/AppLayout';

// Sanitizes event_data. Note: draft_max_reached_step and draft_step_index must be preserved for step-aware dirty tracking.
const stripTransientEventDataKeys = (data = {}) => {
  const cleaned = { ...(data || {}) };
  delete cleaned.__focusThemeKey;
  delete cleaned.__returnThemeKey;
  return cleaned;
};

const AUTH_INTENT_STORAGE_KEY = 'craftmyparty_auth_intent';

const readStoredAuthIntent = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(AUTH_INTENT_STORAGE_KEY);
  } catch (_) {
    return null;
  }
};

const writeStoredAuthIntent = (intent) => {
  if (typeof window === 'undefined') return;
  try {
    if (intent) {
      window.sessionStorage.setItem(AUTH_INTENT_STORAGE_KEY, intent);
    } else {
      window.sessionStorage.removeItem(AUTH_INTENT_STORAGE_KEY);
    }
  } catch (_) {}
};

const normalizePathname = () =>
  typeof window !== 'undefined'
    ? window.location.pathname.replace(/\/+$/, '') || '/'
    : '/';

// Main App Content Component
function AppContent() {
  const initialRoute = useMemo(() => getInitialAppRouteState(), []);
  const [currentPathname, setCurrentPathname] = useState(normalizePathname);
  const { isAuthenticated, user, loading, refreshUser, logout, handleExpiredSession } = useAuth();

  const authIntentRef = useRef(readStoredAuthIntent());

  const setAuthIntent = useCallback((intent) => {
    authIntentRef.current = intent;
    writeStoredAuthIntent(intent);
  }, []);

  // Capture incoming promo or referral codes from query parameters on boot
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const incomingCode = urlParams.get('promo') || urlParams.get('ref');
      if (incomingCode) {
        sessionStorage.setItem('cmp_pending_promo', incomingCode.trim().toUpperCase());
      }
    } catch (_) {}
  }, []);

  const performSignOut = useCallback(async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Error signing out', err);
    } finally {
      authIntentRef.current = null;
      writeStoredAuthIntent(null);
      resetAll();
      setShowLanding(true);
      setCurrentView('home');
      setForceLanding(true);
      setSidebarCollapsed(true);
      if (typeof window !== 'undefined') {
        window.history.pushState({}, '', '/home');
        setCurrentPathname('/home');
      }
    }
  }, [logout]);

  const handleStartPlanning = useCallback(() => {
    setForceLanding(false);
    setSidebarCollapsed(true);
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', '/');
      setCurrentPathname('/');
    }
    startNewPlanFlow();
  }, []);

  const handleGoToBilling = useCallback(() => {
    setForceLanding(false);
    setShowLanding(true);
    setCurrentView('billing');
    setSidebarCollapsed(true);
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', '/billing');
      setCurrentPathname('/billing');
    }
  }, []);

  const [showSessionEndedModal, setShowSessionEndedModal] = useState(false);

  const returnToPublicHomeAfterSessionEnd = useCallback(() => {
    setShowSessionEndedModal(false);
    authIntentRef.current = null;
    resetAll();
    setShowLanding(true);
    setCurrentView('home');
    setForceLanding(true);
    setSidebarCollapsed(true);
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', '/home');
      setCurrentPathname('/home');
    }
  }, [resetAll]);

  const [showLanding, setShowLanding] = useState(initialRoute.showLanding);
  const [forceLanding, setForceLanding] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('home') === 'true';
    }
    return false;
  });
  const [currentScreen, setCurrentScreen] = useState(initialRoute.currentScreen);
  const [eventData, setEventData] = useState(null);
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themePlans, setThemePlans] = useState({});
  const [themeSelectionPreferences, setThemeSelectionPreferences] = useState(null);
  const [themeCarouselFocusKey, setThemeCarouselFocusKey] = useState('');
  const [showThemeOptionsFromDetails, setShowThemeOptionsFromDetails] = useState(false);
  const planTabSessionRef = useRef({});
  const [planActiveTab, setPlanActiveTab] = useState('overview');

  const normalizePlanTabForLayout = useCallback((tab) => {
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      if (tab === 'details') return 'overview';
    }
    return tab || 'overview';
  }, []);
  const [inputResetKey, setInputResetKey] = useState(0);
  const [saveSuccessToken, setSaveSuccessToken] = useState(0);
  const [goToStep16Key] = useState(0);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  // Favorites for the currently loaded plan. Persisted via the lightweight
  // PATCH /api/plans/{id}/favorite endpoint so flipping a star doesn't
  // trigger a full plan resave.
  const [currentPlanIsFavorite, setCurrentPlanIsFavorite] = useState(false);
  const [currentPlanFavoriteThemeKeys, setCurrentPlanFavoriteThemeKeys] = useState([]);
  const [currentView, setCurrentView] = useState(initialRoute.currentView);
  const [bootRestore, setBootRestore] = useState(initialRoute.bootRestore);
  const [isRouteHydrating, setIsRouteHydrating] = useState(Boolean(initialRoute.bootRestore));
  const bootRestorePlanIdRef = useRef(initialRoute.restorePlanId);
  const bootRestoreScreenRef = useRef(initialRoute.restoreScreen);
  const suppressHistoryPushRef = useRef(true);
  const hasSyncedInitialHistoryRef = useRef(false);
  const prevShowLandingRef = useRef(initialRoute.showLanding);
  const handleLoadPlanRef = useRef(null);
  const [previousView, setPreviousView] = useState(null);
  const [, setCurrentPage] = useState('app'); // 'app', 'privacy', 'terms'

  // Modal States
  const [authMode, setAuthMode] = useState('signup');
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [sessionExpiredLanding, setSessionExpiredLanding] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    onCancel: () => { },
    onThirdAction: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    thirdActionText: 'Save and Exit',
    variant: 'default'
  });
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleSignOut = useCallback(() => {
    setConfirmationModal(prev => ({
      ...prev,
      isOpen: true,
      title: 'Sign out?',
      message: 'Your work is saved automatically. You can sign back in anytime.',
      confirmText: 'Sign out',
      cancelText: 'Stay signed in',
      thirdActionText: '',
      variant: 'danger',
      onConfirm: performSignOut,
      onCancel: () => { },
      onThirdAction: null
    }));
  }, [performSignOut]);

  // Direct Flow State
  const [showDirectPlanModal, setShowDirectPlanModal] = useState(false);
  const [pendingDiscardAction, setPendingDiscardAction] = useState(null);
  const [pendingPostSaveDestination, setPendingPostSaveDestination] = useState(null);
  const [pendingDraftData, setPendingDraftData] = useState(null); // event data waiting to be saved via modal
  const pendingDraftDataRef = useRef(null);

  const [shoppingItems, setShoppingItems] = useState([]);
  const [, setInputSwipeHandlers] = useState({ onBack: null, onNext: null });
  const eventInputScreenRef = useRef(null); // Ref to access EventInputScreen methods
  const lazyThemeImageRequestsRef = useRef(new Set());
  const [themeEditModal, setThemeEditModal] = useState({ isOpen: false, theme: null });
  const [inlineBillingHost, setInlineBillingHost] = useState(null);

  const [showDemoMenu, setShowDemoMenu] = useState(false); // Menu drawer for demo users
  const [canSave, setCanSave] = useState(false); // Track if there are unsaved changes to enable Save button
  const planContentDirtyRef = useRef(false);
  const hasRestoredAsyncJobRef = useRef(false);
  // navigationKey is bumped whenever the user navigates to a new screen/plan/theme.
  // AppLayout listens to this to force a scroll-reset even when currentView doesn't change
  // (e.g. switching between plans while staying on the 'plan' screen).
  const [navigationKey, setNavigationKey] = useState(0);
  const bumpNavigationKey = useCallback(() => {
    setNavigationKey(prev => prev + 1);
  }, []);
  // Track the previous selected theme key so we can bump navigationKey when switching
  // between plans even while staying on the 'plan' screen.
  const previousSelectedThemeKeyRef = useRef(null);

  const markPlanContentDirty = () => {
    planContentDirtyRef.current = true;
    markUnsavedChanges();
    setCanSave(true);
  };
  // Sidebar State (Lifted from AppLayout)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      // Force collapsed on small screens regardless of saved state
      if (window.innerWidth < 1024) return true;

      const savedState = localStorage.getItem('sidebarCollapsed');
      if (savedState !== null) return savedState === 'true';
      return false; // Default to expanded on large screens
    }
    return false;
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const newState = !prev;
      if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
        localStorage.setItem('sidebarCollapsed', String(newState));
      }
      return newState;
    });
  }, []);

  // Bump navigationKey whenever the currentScreen or the currently-viewed plan changes.
  // This forces AppLayout's <main> to scroll to the top even when currentView stays the
  // same (e.g. switching plans while staying on the 'plan' screen, or jumping from the
  // themes screen back to the plan screen).
  useEffect(() => {
    const themeKey = getThemePlanKey(selectedTheme) || '';
    const screenChanged = previousSelectedThemeKeyRef.current?.screen !== currentScreen;
    if (screenChanged) {
      previousSelectedThemeKeyRef.current = { screen: currentScreen, themeKey };
      bumpNavigationKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, selectedTheme]);

  // Also bump when entering/leaving the landing/dashboard view.
  useEffect(() => {
    bumpNavigationKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLanding, currentView]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 1024) {
        // ALWAYS collapsed on mobile/tablet screens
        if (!sidebarCollapsed) {
          setSidebarCollapsed(true);
        }
      } else {
        // Restore desktop preference
        const savedState = localStorage.getItem('sidebarCollapsed');
        if (savedState !== null) {
          const shouldBeCollapsed = savedState === 'true';
          if (sidebarCollapsed !== shouldBeCollapsed) {
            setSidebarCollapsed(shouldBeCollapsed);
          }
        }
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarCollapsed]);

  const handleSessionExpiredRedirect = useCallback(() => {
    try {
      localStorage.setItem('craftmyparty_session_recovery', JSON.stringify({
        savedAt: Date.now(),
        userEmail: user?.email || null,
        currentScreen,
        currentView,
        currentPlanId
      }));
    } catch (_) {}

    // Close any open save modal to avoid confusing state
    setShowSaveModal(false);
    // Preserve unsaved data so we can offer to save after re-login
    if (checkUnsavedChanges() || canSave) {
      setPendingAction('save');
      setPendingSaveData({ active: true, name: eventData?.plan_name || eventData?.event_type || 'Draft' });
    }
    // Perform clean local logout so AuthContext state is synchronized and localStorage tokens are cleared
    try {
      logout();
    } catch (_) {}
    // Hide private app state on expiry. The recovery snapshot above is kept
    // for a future signed-in restore, but the visible screen becomes public.
    setAuthMode('signin');
    setIsSessionExpired(true);
    setShowAuthModal(false);
    setShowShareModal(false);
    setThemeEditModal({ isOpen: false, theme: null });
    setConfirmationModal({ isOpen: false, title: '', message: '', onConfirm: null });
    setSessionExpiredLanding(true);
    setForceLanding(false);
    setCurrentView('home');
    setShowLanding(true);
    setCurrentScreen('input');
    setEventData(null);
    setThemes([]);
    setSelectedTheme(null);
    setThemePlans({});
    setThemeSelectionPreferences(null);
    setShoppingItems([]);
    setCurrentPlanId(null);
    setCanSave(false);
    // Do not clear nav snapshot here — transient 401 during refresh-restore
    // can fire session-expired before tokens refresh.
  }, [
    canSave,
    currentPlanId,
    currentScreen,
    currentView,
    eventData,
    selectedTheme,
    shoppingItems,
    logout,
    themePlans,
    themes,
    user
  ]);

  // Global 401/session-expired handler — fired by handleExpiredSession in AuthContext
  useEffect(() => {
    const saveSessionRecoverySnapshot = () => {
      try {
        localStorage.setItem('craftmyparty_session_recovery', JSON.stringify({
          savedAt: Date.now(),
          userEmail: user?.email || null,
          currentScreen,
          currentView,
          currentPlanId
        }));
      } catch (_) {}
    };

    window.addEventListener('craftmyparty:session-expiring', saveSessionRecoverySnapshot);
    window.addEventListener('craftmyparty:session-expired', handleSessionExpiredRedirect);
    return () => {
      window.removeEventListener('craftmyparty:session-expiring', saveSessionRecoverySnapshot);
      window.removeEventListener('craftmyparty:session-expired', handleSessionExpiredRedirect);
    };
  }, [handleSessionExpiredRedirect, currentScreen, currentView, currentPlanId, user]);

  // Reactive protected-view guard to prevent unauthenticated users from staying on protected screens
  useEffect(() => {
    if (loading) return;
    if (isRouteHydrating || bootRestore) return;
    if (isAuthenticated) return;

    // Unauthenticated users are allowed on public landing pages (showLanding === true)
    // and in the wizard screens (showLanding === false && currentScreen !== 'plan')
    const isProtected = !showLanding && (
      currentScreen === 'plan' ||
      ['settings', 'billing', 'plans'].includes(currentView)
    );
    if (!isProtected) return;

    const hasUnsaved = checkUnsavedChanges() || canSave || planContentDirtyRef.current;
    if (hasUnsaved) {
      setShowSessionEndedModal(true);
      setSidebarCollapsed(true);
      return;
    }

    returnToPublicHomeAfterSessionEnd();
  }, [
    loading,
    isRouteHydrating,
    bootRestore,
    isAuthenticated,
    showLanding,
    currentScreen,
    currentView,
    canSave,
    returnToPublicHomeAfterSessionEnd,
  ]);

  // URL Cleanup upon logout or session-expiry to prevent unauthenticated users from staying on stale plan/step URLs
  useEffect(() => {
    if (loading) return;
    if (isAuthenticated) return;

    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    
    // Public routes that must remain completely untouched
    const isSpecialPublicPath = ['/privacy', '/terms', '/faq', '/shared', '/share', '/p/', '/occasions'].some(p => path.startsWith(p));
    if (isSpecialPublicPath) return;

    const hasStaleParams = window.location.search && (
      window.location.search.includes('plan=') ||
      window.location.search.includes('step=') ||
      window.location.search.includes('draft=')
    );

    const isAllowedPublicPath = path === '/' || path === '/home' || path === '/home/';

    if (hasStaleParams || !isAllowedPublicPath) {
      window.history.replaceState({}, '', '/home');
      setCurrentPathname('/home');
      setShowLanding(true);
      setCurrentView('home');
      setForceLanding(true);
      setSidebarCollapsed(true);
    }
  }, [loading, isAuthenticated, currentPathname]);

  const finishRouteHydration = useCallback(() => {
    setBootRestore(null);
    setIsRouteHydrating(false);
  }, []);

  const hydratePlanById = useCallback(async (planId) => {
    await authUtils.getFreshAuthHeader();
    return planUtils.getPlan(planId);
  }, []);

  // Hydrate plan after refresh. Re-runs when auth becomes ready (Strict Mode safe).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!bootRestore) return undefined;

    const planId = bootRestorePlanIdRef.current;
    if (!planId) {
      finishRouteHydration();
      return undefined;
    }

    if (!authUtils.isAuthenticated()) {
      finishRouteHydration();
      return undefined;
    }
    if (loading && !user) return undefined;

    let cancelled = false;
    const targetScreen = bootRestoreScreenRef.current;

    (async () => {
      try {
        let plan = await hydratePlanById(planId);
        if (cancelled) return;
        if (plan) {
          handleLoadPlanRef.current?.(plan);
          if (targetScreen && ['plan', 'carousel', 'input'].includes(targetScreen)) {
            setCurrentScreen(targetScreen);
          }
          finishRouteHydration();
          return;
        }
      } catch (err) {
        if (!cancelled && handleExpiredSession) {
          const result = await handleExpiredSession();
          if (result === 'refreshed') {
            try {
              const plan = await hydratePlanById(planId);
              if (cancelled) return;
              if (plan) {
                handleLoadPlanRef.current?.(plan);
                if (targetScreen && ['plan', 'carousel', 'input'].includes(targetScreen)) {
                  setCurrentScreen(targetScreen);
                }
                finishRouteHydration();
                return;
              }
            } catch (_) {}
          }
        }
        if (!cancelled) clearNavSnapshot();
      } finally {
        if (!cancelled) finishRouteHydration();
      }
    })();

    return () => { cancelled = true; };
  }, [bootRestore, loading, isAuthenticated, user, finishRouteHydration, hydratePlanById, handleExpiredSession]);

  const applyHistoryRoute = useCallback((route) => {
    if (!route) return;
    setForceLanding(false);
    setShowLanding(Boolean(route.showLanding));
    setCurrentView(route.currentView || 'dashboard');
    setCurrentScreen(route.currentScreen || 'input');
    const nextPlanId = route.planId || null;
    if (!route.showLanding && nextPlanId && nextPlanId !== currentPlanId) {
      setIsRouteHydrating(true);
      (async () => {
        try {
          const plan = await hydratePlanById(nextPlanId);
          if (plan) {
            handleLoadPlanRef.current?.(plan);
            if (route.currentScreen) setCurrentScreen(route.currentScreen);
          }
        } catch (_) {}
        setIsRouteHydrating(false);
      })();
    }
  }, [currentPlanId, hydratePlanById]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPopState = (event) => {
      const poppedRoute = readHistoryRoute(event);
      const isExitGuard = poppedRoute?.isExitGuard === true;
      const hasUnsaved = checkUnsavedChanges() || canSave;

      if (isExitGuard) {
        if (hasUnsaved) {
          // ── Popstate sentinel guard: user hit the guard entry while wizard is dirty ──

          // 1. Immediately restore the active wizard route (push back to wizard details/screen)
          suppressHistoryPushRef.current = true;
          pushAppHistory({
            showLanding: false,
            currentView: 'dashboard',
            currentScreen: currentScreen,
            planId: currentPlanId
          }, { replace: false });
          window.requestAnimationFrame(() => {
            suppressHistoryPushRef.current = false;
          });

          // 2. Capture draft data if on input screen
          if (currentScreen === 'input' && eventInputScreenRef.current) {
            try {
              const latest = eventInputScreenRef.current.getCurrentFormData();
              const sanitized = stripTransientEventDataKeys(ensurePlanEstimateCore(latest || {}));
              const hasValidEventType = !!(sanitized?.event_type || latest?.event_type);
              if (hasValidEventType) {
                pendingDraftDataRef.current = sanitized;
                setPendingDraftData(sanitized);
              }
            } catch (_) { /* guard against ref teardown */ }
          }

          // 3. Show save/discard modal directly
          const dest = poppedRoute?.currentView || 'dashboard';
          setPendingPostSaveDestination(dest);
          setPendingDiscardAction(() => () => {
            clearUnsavedChanges();
            navigateAfterWizardExit(dest);
          });
          setShowSaveModal(true);
          return;
        } else {
          // Guard is hit but wizard is NOT dirty: cleanly pass through the guard by going back one more step
          window.history.back();
          return;
        }
      }

      suppressHistoryPushRef.current = true;
      applyHistoryRoute(poppedRoute);
      setCurrentPathname(normalizePathname());
      window.requestAnimationFrame(() => {
        suppressHistoryPushRef.current = false;
      });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [applyHistoryRoute, showLanding, currentView, currentScreen, currentPlanId, canSave]);

  useEffect(() => {
    if (typeof window === 'undefined' || loading) return;
    if (suppressHistoryPushRef.current) {
      prevShowLandingRef.current = showLanding;
      return;
    }

    const enteringWizard = prevShowLandingRef.current && !showLanding;
    const exitingWizard = !prevShowLandingRef.current && showLanding;
    prevShowLandingRef.current = showLanding;

    if (enteringWizard) {
      // 1. Push the exit guard entry first
      pushAppHistory({
        showLanding: false,
        currentView: currentView || 'dashboard',
        currentScreen: currentScreen || 'input',
        planId: currentPlanId || null,
        isExitGuard: true
      }, { replace: false });

      // 2. Then push the actual active wizard entry
      pushAppHistory({
        showLanding: false,
        currentView: currentView || 'dashboard',
        currentScreen: currentScreen || 'input',
        planId: currentPlanId || null
      }, { replace: false });
    } else if (exitingWizard) {
      pushAppHistory({
        showLanding,
        currentView,
        currentScreen,
        planId: currentPlanId
      }, { replace: true });
    } else {
      pushAppHistory({
        showLanding,
        currentView,
        currentScreen,
        planId: currentPlanId
      });
    }
  }, [showLanding, currentView, currentScreen, currentPlanId, loading]);

  useEffect(() => {
    if (typeof window === 'undefined' || loading) return;
    if (hasSyncedInitialHistoryRef.current) return;
    hasSyncedInitialHistoryRef.current = true;
    suppressHistoryPushRef.current = true;
    pushAppHistory({
      showLanding,
      currentView,
      currentScreen,
      planId: currentPlanId
    }, { replace: true });
    window.requestAnimationFrame(() => {
      suppressHistoryPushRef.current = false;
    });
  }, [loading, showLanding, currentView, currentScreen, currentPlanId]);

  // Persist where the user was so refresh restores the same shell (dashboard
  // tab, settings, or wizard step) without flashing dashboard first.
  useEffect(() => {
    if (typeof window === 'undefined' || loading || !isAuthenticated) return;
    writeNavSnapshot({
      showLanding,
      currentView,
      currentScreen,
      planId: currentPlanId
    });
  }, [showLanding, currentView, currentScreen, currentPlanId, loading, isAuthenticated]);

  // Scroll position preservation: store positions per screen
  const swipeAreaRef = useRef(null);

  const scrollAppToTop = useCallback((behavior = 'auto') => {
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('main');
      if (scrollContainer?.scrollTo) {
        scrollContainer.scrollTo({ top: 0, left: 0, behavior });
      }
      window.scrollTo({ top: 0, left: 0, behavior });
    });
  }, []);

  useEffect(() => {
    scrollAppToTop('auto');
  }, [currentScreen, currentView, showLanding, scrollAppToTop]);

  useEffect(() => {
    if (currentScreen !== 'plan') {
      planTabSessionRef.current = {};
    }
  }, [currentScreen]);

  useEffect(() => {
    if (!showLanding) {
      setSidebarCollapsed(true);
    }
  }, [showLanding]);

  useEffect(() => {
    const handleMultiTabLogout = () => {
      const hasUnsaved =
        checkUnsavedChanges() ||
        canSave ||
        planContentDirtyRef.current;

      if (hasUnsaved) {
        setShowSessionEndedModal(true);
        setSidebarCollapsed(true);
        return;
      }

      returnToPublicHomeAfterSessionEnd();
    };

    window.addEventListener('craftmyparty:multi-tab-logout', handleMultiTabLogout);
    return () => {
      window.removeEventListener('craftmyparty:multi-tab-logout', handleMultiTabLogout);
    };
  }, [canSave, returnToPublicHomeAfterSessionEnd]);

  const wasAuthenticatedRef = useRef(false);

  useEffect(() => {
    const wasAuthenticated = wasAuthenticatedRef.current;

    if (isAuthenticated && !wasAuthenticated) {
      const intent = authIntentRef.current || readStoredAuthIntent();
      authIntentRef.current = null; // consume it immediately
      writeStoredAuthIntent(null);

      if (intent) {
        if (intent === 'startPlanning' || intent === 'demo') {
          setForceLanding(false);
          setSidebarCollapsed(true);
          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', '/');
            setCurrentPathname('/');
          }
          startNewPlanFlow();
        } else if (intent === 'signin') {
          setForceLanding(false);
          setShowLanding(true);
          setCurrentView('dashboard');
          setSidebarCollapsed(true);

          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', '/');
            setCurrentPathname('/');
          }
        }
      }
    }

    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const handlePlanActiveTabChange = useCallback((tab) => {
    setPlanActiveTab(tab);
    const key = getThemePlanKey(selectedTheme);
    if (key) planTabSessionRef.current[key] = tab;
  }, [selectedTheme]);

  // Handlers (omitted for brevity, assuming they exist as before)
  const viewLabels = {
    home: 'Homepage',
    dashboard: 'Dashboard',
    plans: 'Plans',
    shared: 'Shared',
    settings: 'Settings',
    billing: 'Billing & Credits',
    help: 'Help Center'
  };

  const navigateAppView = (view, options = {}) => {
    if (!view) return;
    if (view === 'signin' || view === 'signup') {
      setAuthMode(view);
      setShowAuthModal(true);
      return;
    }
    if (view === 'home') {
      setPreviousView(currentView);
      goHome();
      return;
    }
    if (['dashboard', 'plans', 'shared'].includes(view)) {
      window.__reloadDashboard = true;
    }
    setPreviousView(options.backTo || (currentView !== view ? currentView : previousView) || 'dashboard');
    setForceLanding(false);
    setShowLanding(true);
    setCurrentView(view);
  };

  const exitWizardToView = (destination = 'dashboard', force = false) => {
    if (force) {
      if (destination === 'home') goHome();
      else navigateAfterWizardExit(destination);
      return;
    }

    if (checkUnsavedChanges() || canSave) {
      if (currentScreen === 'input' && eventInputScreenRef.current) {
        const latest = eventInputScreenRef.current.getCurrentFormData();
        const sanitized = stripTransientEventDataKeys(ensurePlanEstimateCore(latest || {}));
        // Part B: Only overwrite eventData when event_type is present.
        // Stripping transient keys can produce an object without event_type for
        // brand-new plans; setting that as eventData causes the dirty-tracking
        // effect to hit the !eventType branch and reset canSave to false,
        // making the next navigation bypass the guard silently.
        const hasValidEventType = !!(sanitized?.event_type || latest?.event_type);
        if (hasValidEventType) {
          pendingDraftDataRef.current = sanitized;
          setPendingDraftData(sanitized);
          // Do NOT call setEventData(sanitized) here. EventInputScreen receives
          // initialEventData={eventData}, and mutating eventData mid-flow makes
          // it look like a loaded draft, which trips the 1500ms restore branch
          // in the dirty-tracking effect and resets canSave/checkUnsavedChanges
          // to false — causing the next navigation attempt to bypass the guard.
          // The modal already uses: pendingDraftDataRef.current || pendingDraftData || eventData
          // so it gets the correct data without touching eventData.
        } else {
          console.warn('[exitWizardToView] Skipping draft capture: event_type missing', { latest, sanitized });
        }
      }

      // Show "Save Plan" modal which acts as the Unsaved Changes prompt (Save vs Discard)
      setPendingPostSaveDestination(destination);
      setPendingDiscardAction(() => () => {
        clearUnsavedChanges();
        navigateAfterWizardExit(destination);
      });
      setShowSaveModal(true);
      return;
    }
    navigateAfterWizardExit(destination);
  };

  const handleGoToHome = async (force = false) => {
    exitWizardToView('home', force);
  };

  // Manage body class for modal stacking context
  useEffect(() => {
    const anyModalOpen = showDirectPlanModal || showSaveModal || showAuthModal || showShareModal;
    if (anyModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [showDirectPlanModal, showSaveModal, showAuthModal, showShareModal]);

  const goHome = () => {
    setForceLanding(true);
    setCurrentView('home');
    setShowLanding(true);
    setCurrentScreen('input');
    resetAll();
  };

  function navigateAfterWizardExit(destination = 'dashboard', savedPlanId = null) {
    // Part C: 'signout' is a special pseudo-destination that clears workflow
    // state and then triggers the actual sign-out flow.
    if (destination === 'signout') {
      resetAll();
      performSignOut();
      return;
    }
    resetAll();
    setShowLanding(true);
    if (savedPlanId) {
      setHighlightPlanId(savedPlanId);
      window.__reloadDashboard = true;
    }
    if (destination === 'home') {
      setForceLanding(true);
      setCurrentView('home');
    } else {
      setForceLanding(false);
      setPreviousView('dashboard');
      setCurrentView(destination);
    }
  }

  const handleStartOver = () => {
    setConfirmationModal({
      isOpen: true,
      title: 'Start Over',
      message: 'Are you sure you want to start over? All current progress will be lost.',
      onConfirm: () => {
        resetAll();
      },
      confirmText: 'Start Over',
      variant: 'danger'
    });
  };

  function resetAll() {
    clearInFlightThemeJob();
    clearInFlightPlanJob();
    clearNavSnapshot();
    setEventData(null);
    setThemes([]);
    setSelectedTheme(null);
    setThemeCarouselFocusKey('');
    setThemePlans({});
    setThemeSelectionPreferences(null);
    setShoppingItems([]);
    setCurrentPlanId(null);
    setShowThemeOptionsFromDetails(false);
    try {
      planUtils.setCurrentPlanId(null);
    } catch (e) {
      console.warn('Unable to clear planUtils current plan ID:', e);
    }
    setCurrentPlanIsFavorite(false);
    setCurrentPlanFavoriteThemeKeys([]);
    setCurrentScreen('input');
    window.__restoreStep = null;
    window.__restoreTime = null;
    setInputResetKey(prev => prev + 1);
    clearUnsavedChanges();
    setCanSave(false);
    planContentDirtyRef.current = false;
    setShowSaveModal(false);
    pendingDraftDataRef.current = null;
    setPendingDraftData(null);
    setPendingDiscardAction(null);
    setPendingPostSaveDestination(null);
    setPendingAction(null);
    // Clear active steps from sessionStorage
    try {
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('cmp_active_step_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.error(e);
    }
  };


  function startNewPlanFlow() {
    resetAll();

    try {
      localStorage.removeItem('craftmyparty_session_recovery');
      sessionStorage.removeItem('craftmyparty_session_recovery');
    } catch (e) {
      console.warn('Unable to clear session recovery snapshot:', e);
    }

    try {
      planUtils.setCurrentPlanId(null);
    } catch (e) {
      console.warn('Unable to clear planUtils current plan ID:', e);
    }

    try {
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('cmp_active_step_')) {
          sessionStorage.removeItem(key);
        }
      });
      sessionStorage.removeItem('cmp_active_step_last');
    } catch (e) {
      console.warn('Unable to clear wizard step storage:', e);
    }

    window.__restoreStep = 1;
    window.__restoreTime = Date.now();
    setInputResetKey(prev => prev + 1);

    setShowLanding(false);
    setCurrentScreen('input');
    setCurrentView('dashboard');
    setSidebarCollapsed(true);
  }

  const handleEventSubmit = async (data) => {
    const nextEventData = { ...(data || {}) };
    setEventData(nextEventData);

    let planningFlow = null;
    try {
      planningFlow = getPlanningFlow(nextEventData);
      console.debug('[CMP_PLANNING_FLOW]', planningFlow);
    } catch (err) {
      console.error('Error computing planning flow:', err);
    }

    if (nextEventData.__recalculateCurrentPlan && selectedTheme) {
      setCurrentScreen('loadingDetails');
      return;
    }

    let shouldBypassQuickWorkflowForFoodSurpriseMe = false;
    let shouldBypassQuickWorkflowForDecorSurpriseMe = false;
    if (planningFlow) {
      const isFoodIntent = planningFlow.primaryIntent === 'food';
      const isDecorIntent = planningFlow.primaryIntent === 'decor';
      const isConceptFirst = planningFlow.recommendedFutureMode === 'CONCEPT_FIRST';
      
      if (isFoodIntent && isConceptFirst) {
        console.debug('[CMP_CONCEPT_FIRST_FOOD]', {
          focusSelection: planningFlow.focusSelection,
          primaryIntent: planningFlow.primaryIntent,
          recommendedFutureMode: planningFlow.recommendedFutureMode,
          isSurpriseMe: planningFlow.isSurpriseMe,
          hasKnownTheme: planningFlow.hasKnownTheme,
          theme_input: nextEventData.theme_input,
          event_type: nextEventData.event_type,
          custom_event_type: nextEventData.custom_event_type,
          guest_count: nextEventData.guest_count,
          budget: nextEventData.budget
        });
        shouldBypassQuickWorkflowForFoodSurpriseMe = true;
      }

      if (isDecorIntent && isConceptFirst) {
        console.debug('[CMP_CONCEPT_FIRST_DECOR]', {
          focusSelection: planningFlow.focusSelection,
          primaryIntent: planningFlow.primaryIntent,
          recommendedFutureMode: planningFlow.recommendedFutureMode,
          isSurpriseMe: planningFlow.isSurpriseMe,
          hasKnownTheme: planningFlow.hasKnownTheme,
          theme_input: nextEventData.theme_input,
          event_type: nextEventData.event_type,
          custom_event_type: nextEventData.custom_event_type,
          guest_count: nextEventData.guest_count,
          budget: nextEventData.budget
        });
        shouldBypassQuickWorkflowForDecorSurpriseMe = true;
      }
    }

    const focus = Array.isArray(nextEventData.focus_selection) ? nextEventData.focus_selection : [];
    const quickWorkflowMap = {
      decor: 'decor',
      food: 'food',
      activities: 'activities',
      shopping: 'shopping'
    };
    const quickFocus = focus.length > 0
      && !focus.includes('everything')
      && !focus.includes('theme_ideas')
      && focus.every(item => quickWorkflowMap[item]);

    const hasFoodCustomCounts = focus.includes('food') &&
      nextEventData.food_menu_structure &&
      typeof nextEventData.food_menu_structure === 'object' &&
      Object.entries(nextEventData.food_menu_structure).some(([mealKey, count]) => {
        const selectedMeals = Array.isArray(nextEventData.meals) ? nextEventData.meals : [];
        return selectedMeals.includes(mealKey) && count > 1;
      });

    if (quickFocus && !shouldBypassQuickWorkflowForFoodSurpriseMe && !shouldBypassQuickWorkflowForDecorSurpriseMe && !hasFoodCustomCounts) {
      try {
        const workflow = focus.length === 1 ? quickWorkflowMap[focus[0]] : 'shopping';
        const response = await fetch(`${BACKEND_URL}/api/catalog/quick-workflow`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workflow,
            eventData: nextEventData,
            limit: workflow === 'activities' ? 18 : 24
          })
        });
        if (!response.ok) throw new Error('Catalog workflow failed');
        const result = await response.json();
        const quickTheme = {
          id: `quick-${workflow}-${Date.now()}`,
          name: result.title || 'Catalog-backed plan',
          title: result.title || 'Catalog-backed plan',
          description: result.summary || 'Catalog-backed recommendations with estimated quantities, costs, and sourcing routes.',
          color_palette: ['#7C3AED', '#F97316', '#14B8A6', '#F8FAFC'],
          categories: {},
          planner_rationale: {
            budget_fit: 'Catalog quantity rules are used before saving recommendations.',
            guest_experience: 'The list is scoped to the planning focus you selected.',
            sourcing_strategy: 'Sourcing paths follow food, decor, activity, and rental-specific rules.'
          },
          catalog_quick_workflow: workflow
        };
        setSelectedTheme(quickTheme);
        setThemes([quickTheme]);
        setShoppingItems(result.shoppingList || []);
        setThemePlans({});

        // Auto-save quick-workflow plan if authenticated
        if (isAuthenticated) {
          const planDataToSave = {
            event_data: stripTransientEventDataKeys(nextEventData),
            themes: [quickTheme],
            selected_theme: quickTheme,
            shopping_items: result.shoppingList || [],
            theme_plans: {}
          };
          try {
            let saveResult;
            if (currentPlanId) {
              saveResult = await planUtils.updatePlan(currentPlanId, planDataToSave);
            } else {
              saveResult = await planUtils.createPlan(planDataToSave);
              const newId = saveResult?.plan_id;
              if (newId) setCurrentPlanId(newId);
            }
            clearUnsavedChanges();
            setCanSave(false);
            try { window.__reloadDashboard = true; } catch (_) {}
          } catch (err) {
            console.error('Quick-workflow auto-save failed:', err);
          }
        }

        setCurrentScreen('plan');
        setPlanActiveTab(workflow === 'decor' || workflow === 'shopping' ? 'shopping' : workflow === 'food' ? 'menu' : 'activities');
        return;
      } catch (error) {
        console.error('Quick catalog workflow failed:', error);
      }
    }
    setCurrentScreen('loading');
  };

  const handleThemesGenerated = async (generatedThemes, _userInputProvided = false, generationEventData = null) => {
    clearInFlightThemeJob();
    const sourceEventData = generationEventData || eventData || {};
    trackEvent('themes_generated', {
      theme_count: generatedThemes?.length || 0,
      occasion: sourceEventData?.event_type || sourceEventData?.occasion || 'Celebration',
      guest_count: sourceEventData?.guest_count || null,
    });
    const shouldAppendThemes = Boolean(sourceEventData?.__appendThemes);
    const similarParentKey = sourceEventData?.__similarToThemeKey || '';
    // Only set variant_of_theme_name when we have an actual parent theme key —
    // i.e. when the user clicked "More like this". For "fresh set" we must
    // NOT fall back to theme_input because the ThemeCarousel display logic
    // (themeGroups useMemo) prefers variant_of_theme_name and overrides the
    // Set N label, making fresh sets appear as "Inspired by ...".
    const isSimilarMode = Boolean(similarParentKey);
    const similarParentName = isSimilarMode
      ? (sourceEventData?.__similarToThemeName || sourceEventData?.theme_input || '')
      : '';
    const themeSetId = sourceEventData?.__themeSetId || '';
    const themeSetLabel = sourceEventData?.__themeSetLabel || '';
    const themesToMerge = shouldAppendThemes
      ? (generatedThemes || []).map((theme, index) => ({
          ...theme,
          source_theme_id: theme?.id || '',
          id: `variant-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
          ...(isSimilarMode
            ? { variant_of_theme_key: similarParentKey, variant_of_theme_name: similarParentName }
            : {}),
          ...(themeSetId ? { theme_set_id: themeSetId } : {}),
          ...(themeSetLabel ? { theme_set_label: themeSetLabel } : {})
        }))
      : generatedThemes;
    const nextThemes = shouldAppendThemes
      ? [
          ...(themes || []),
          ...(themesToMerge || [])
        ]
      : themesToMerge;
    const appendedThemes = shouldAppendThemes ? nextThemes.slice((themes || []).length) : [];
    const focusThemeKey = appendedThemes.length > 0
      ? getThemePlanKey(appendedThemes[0])
      : String(sourceEventData?.__returnThemeKey || '').trim();

    setThemes(nextThemes);
    if (shouldAppendThemes) {
      if (focusThemeKey) {
        setThemeCarouselFocusKey(focusThemeKey);
      }
      setEventData(prev => ({
        ...(prev || sourceEventData || {}),
	        __appendThemes: false,
	        __openFreshSetWorkflow: false,
	        __returnToThemesAfterFreshSet: false,
	        __similarToThemeKey: '',
	        __similarToThemeName: '',
	        __freshSetRequestId: '',
	        __focusThemeKey: focusThemeKey,
	        __returnThemeKey: focusThemeKey || prev?.__returnThemeKey || sourceEventData?.__returnThemeKey || ''
	      }));
    }
    if (!shouldAppendThemes) {
      setThemePlans({});
    }
    const focusSel = sourceEventData?.focus_selection || eventData?.focus_selection || [];
    const isThemeBased = !focusSel || focusSel.length === 0 || focusSel.includes('everything') || focusSel.includes('theme_ideas');

    if (isThemeBased) {
      setCurrentScreen('carousel');
    } else {
      if (nextThemes && nextThemes.length > 0) {
        const firstTheme = nextThemes[0];
        setSelectedTheme(firstTheme);
        setCurrentScreen('loadingDetails');
      } else {
        setCurrentScreen('plan');
      }
    }

    // Auto-save generated themes if authenticated
    if (isAuthenticated && (currentPlanId || sourceEventData)) {
      try {
        const planDataToSave = {
          event_data: stripTransientEventDataKeys(sourceEventData),
          themes: nextThemes,
          selected_theme: selectedTheme,
          shopping_items: shoppingItems,
          theme_plans: shouldAppendThemes ? (themePlans || {}) : {}
        };

        let result;
        if (currentPlanId) {
          result = await planUtils.updatePlan(currentPlanId, planDataToSave);
        } else {
          // If no plan ID but we have data, create a new plan
          result = await planUtils.createPlan(planDataToSave);
          const newId = result?.plan_id;
          if (newId) setCurrentPlanId(newId);
        }

        // Mark as clean since we just saved
        clearUnsavedChanges();
      } catch (err) {
        console.error('Theme auto-save failed:', err);
        // Silently fail, user can still save manually
      }
    }

    if (refreshUser) {
      refreshUser({ preserveOnFailure: true }).catch(() => {});
    }
  };


  const navigateToInput = (extraEventData = null) => {
    if (extraEventData && typeof extraEventData === 'object') {
      setEventData(prev => ({
        ...(prev || {}),
        ...extraEventData
      }));
      const nextFocusKey = String(extraEventData.__focusThemeKey || extraEventData.__returnThemeKey || '').trim();
      if (nextFocusKey) {
        setThemeCarouselFocusKey(nextFocusKey);
      }
    }
    if (currentScreen === 'plan') {
      setInputResetKey((prev) => prev + 1);
    }
    setShowThemeOptionsFromDetails(false);
    setCurrentScreen('input');
  };

  const handleLoadingError = (error) => {
    // Clear any leftover fresh-set workflow flags so a failed generation
    // doesn't strand the user behind a stale __returnToThemesAfterFreshSet
    // on the input screen (which previously sent them to the themes screen
    // when they just tried to close the regenerate modal).
    setEventData(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      const {
        __returnToThemesAfterFreshSet,
        __openFreshSetWorkflow,
        __freshSetRequestId,
        ...rest
      } = prev;
      // Only return a new object if any flag was actually set, otherwise React
      // will treat it as a state change.
      if (__returnToThemesAfterFreshSet || __openFreshSetWorkflow || __freshSetRequestId) {
        return rest;
      }
      return prev;
    });
    navigateToInput();
    if (error && (error.response?.status === 429 || (error.message && error.message.includes('limit reached')))) {
      setAuthMode('signup');
      setShowAuthModal(true);
    }
  };

  const generateSelectedThemeImage = useCallback(async (theme) => {
    if (!isAuthenticated || !theme || theme.image_status === 'generated') return;

    const themeKey = theme.id || theme.name;
    if (!themeKey || lazyThemeImageRequestsRef.current.has(themeKey)) return;
    lazyThemeImageRequestsRef.current.add(themeKey);

    try {
      const response = await fetch(`${BACKEND_URL}/api/themes/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authUtils.getAuthHeader()
        },
        body: JSON.stringify({
          theme,
          event_input: eventData,
          plan_id: currentPlanId || null
        })
      });

      if (!response.ok) return;
      const data = await response.json();
      if (!data?.success || !data?.image_url) return;

      const updatedTheme = {
        ...theme,
        ...(data.theme || {}),
        image: data.image_url,
        image_url: data.image_url,
        ai_image_url: data.image_url,
        image_status: data.image_status || 'generated'
      };

      const matchesTheme = (candidate) => (
        (candidate?.id && updatedTheme.id && candidate.id === updatedTheme.id)
        || (candidate?.name && updatedTheme.name && candidate.name === updatedTheme.name)
      );

      setThemes(prevThemes => prevThemes.map(candidate => (
        matchesTheme(candidate) ? { ...candidate, ...updatedTheme } : candidate
      )));

      setSelectedTheme(prevTheme => (
        matchesTheme(prevTheme) ? { ...prevTheme, ...updatedTheme } : prevTheme
      ));
    } catch (error) {
      console.warn('Selected theme image generation skipped:', error.message);
    }
  }, [currentPlanId, eventData, isAuthenticated]);

  const handleThemeSelect = (theme, preferences) => {
    setSelectedTheme(theme);
    setThemeSelectionPreferences(preferences || null);
    generateSelectedThemeImage(theme);
    trackEvent('theme_selected', {
      theme_name: theme?.name || 'Unknown',
      occasion: eventData?.event_type || eventData?.occasion || 'Celebration',
    });

    // If user adjusted the overall budget in the selection modal, update it here
    if (preferences?.newTotalBudget) {
      setEventData(prev => ({
        ...prev,
        budget: preferences.newTotalBudget
      }));
    }

    setShowThemeOptionsFromDetails(false);
    setCurrentScreen('loadingDetails');
  };

  const handleDetailsLoaded = (themeWithDetails) => {
    clearInFlightPlanJob();
    if (themeWithDetails) {
      const generatedItems = initializeItemsFromTheme(themeWithDetails, eventData);
      trackEvent('plan_created', {
        theme_name: themeWithDetails?.name || 'Unknown',
        occasion: eventData?.event_type || eventData?.occasion || 'Celebration',
        shopping_items_count: generatedItems?.length || 0,
      });
      const themePlanKey = getThemePlanKey(themeWithDetails);
      const themePlanSnapshot = {
        selected_theme: themeWithDetails,
        shopping_items: generatedItems,
        updated_at: new Date().toISOString()
      };
      const nextThemePlans = themePlanKey
        ? {
            ...(themePlans || {}),
            [themePlanKey]: themePlanSnapshot
          }
        : themePlans;
      setSelectedTheme(themeWithDetails);
      setThemeSelectionPreferences(null);
      setShoppingItems(generatedItems);
      if (themePlanKey) {
        setThemePlans(nextThemePlans);
      }
      if (themePlanKey) {
        setThemeCarouselFocusKey(themePlanKey);
      }
      setEventData((prev) => withPlanEstimateCoreSnapshot({
        ...(prev || eventData || {}),
        __recalculateCurrentPlan: false,
        ...(themePlanKey ? { __focusThemeKey: themePlanKey, __returnThemeKey: themePlanKey } : {})
      }));
      markUnsavedChanges();
      setCanSave(true);

      if (isAuthenticated && currentPlanId) {
        planUtils.updatePlan(currentPlanId, {
          event_data: stripTransientEventDataKeys(eventData),
          themes,
          selected_theme: themeWithDetails,
          shopping_items: generatedItems,
          theme_plans: nextThemePlans || {}
        })
          .then(() => {
            clearUnsavedChanges();
            setCanSave(false);
          })
          .catch((error) => {
            console.error('Plan auto-save after generation failed:', error);
          });
      }
    }
    if (refreshUser) {
      refreshUser().catch(() => {});
    }
    setCurrentScreen('plan');
  };

  const buildThemePlansForSave = useCallback((selectedThemeOverride = selectedTheme, shoppingItemsOverride = shoppingItems) => {
    const next = { ...(themePlans || {}) };
    const key = getThemePlanKey(selectedThemeOverride);
    if (key && selectedThemeOverride) {
      next[key] = {
        ...(next[key] || {}),
        selected_theme: selectedThemeOverride,
        shopping_items: Array.isArray(shoppingItemsOverride) ? shoppingItemsOverride : [],
        updated_at: new Date().toISOString()
      };
    }
    return next;
  }, [selectedTheme, shoppingItems, themePlans]);

  const handleBackToThemes = () => {
    clearInFlightPlanJob();
    setSelectedTheme(null);
    setThemeSelectionPreferences(null);
    if (themeCarouselFocusKey) {
      setEventData(prev => ({
        ...(prev || eventData || {}),
        __focusThemeKey: themeCarouselFocusKey,
        __returnThemeKey: themeCarouselFocusKey
      }));
    }
    setCurrentScreen('carousel');
  };

  const handleResumePlanning = () => {
    setShowThemeOptionsFromDetails(false);
    setCurrentScreen('input');
    const savedDraftStep = Number(eventData?.draft_step_index);
    window.__restoreStep = (Number.isFinite(savedDraftStep) && savedDraftStep > 0)
      ? savedDraftStep
      : 'firstIncomplete';
    window.__restoreTime = Date.now();
    setInputResetKey(prev => prev + 1);
  };

  const preserveCarouselThemeFocus = (theme) => {
    const focusKey = getThemePlanKey(theme);
    if (!focusKey) return;
    setThemeCarouselFocusKey(focusKey);
    setEventData((prev) => ({
      ...(prev || {}),
      __focusThemeKey: focusKey,
      __returnThemeKey: focusKey
    }));
  };

  const handleThemeRefined = (updatedTheme) => {
    if (!updatedTheme) return;

    const matchesTheme = (candidate) => themesMatch(candidate, updatedTheme);

    setSelectedTheme(prevTheme => (
      matchesTheme(prevTheme) ? { ...prevTheme, ...updatedTheme } : prevTheme
    ));

    setThemes(prevThemes => (prevThemes || []).map(theme => (
      matchesTheme(theme) ? { ...theme, ...updatedTheme } : theme
    )));

    setThemePlans(prev => {
      const next = { ...(prev || {}) };
      Object.entries(next).forEach(([key, snapshot]) => {
        if (matchesTheme(snapshot?.selected_theme)) {
          next[key] = {
            ...snapshot,
            selected_theme: { ...snapshot.selected_theme, ...updatedTheme },
            updated_at: new Date().toISOString()
          };
        }
      });
      return next;
    });

    markPlanContentDirty();
  };

  const handleOpenThemeEdit = (theme) => {
    setThemeEditModal({ isOpen: true, theme });
  };

  const handleSaveThemeEdit = (updatedTheme) => {
    const sourceTheme = themeEditModal.theme;
    const matchesTheme = (candidate) => themesMatch(candidate, sourceTheme);

    setThemes(prevThemes => prevThemes.map(theme => (
      matchesTheme(theme) ? { ...theme, ...updatedTheme } : theme
    )));

    setSelectedTheme(prevTheme => (
      matchesTheme(prevTheme) ? { ...prevTheme, ...updatedTheme } : prevTheme
    ));

    setThemePlans(prev => {
      const next = { ...prev };
      Object.entries(next).forEach(([key, snapshot]) => {
        if (matchesTheme(snapshot?.selected_theme)) {
          next[key] = {
            ...snapshot,
            selected_theme: { ...snapshot.selected_theme, ...updatedTheme },
            updated_at: new Date().toISOString()
          };
        }
      });
      return next;
    });

    preserveCarouselThemeFocus(updatedTheme);
    markPlanContentDirty();
  };

  const handleRegenerateThemes = (latestEventData = null) => {
    if (Array.isArray(latestEventData?.__replaceThemes)) {
      const nextThemes = latestEventData.__replaceThemes;
      const nextEventData = { ...(eventData || {}), ...latestEventData };
      delete nextEventData.__replaceThemes;
      const nextFocusKey = String(nextEventData.__focusThemeKey || nextEventData.__returnThemeKey || '').trim();

      setThemes(nextThemes);
      setEventData(nextEventData);
      if (nextFocusKey) setThemeCarouselFocusKey(nextFocusKey);

      if (isAuthenticated && currentPlanId) {
        planUtils.updatePlan(currentPlanId, {
          event_data: stripTransientEventDataKeys(nextEventData),
          themes: nextThemes,
          selected_theme: selectedTheme,
          shopping_items: shoppingItems,
          theme_plans: themePlans || {}
        })
          .then(() => {
            clearUnsavedChanges();
            setCanSave(false);
          })
          .catch((err) => {
            console.error('Theme deletion save failed:', err);
            markUnsavedChanges();
            setCanSave(true);
          });
      }
      return;
    }

    if (latestEventData) {
      const requestedCount = Number(latestEventData.theme_count);
      const returnThemeKey = String(latestEventData.__returnThemeKey || latestEventData.__focusThemeKey || '').trim();
      if (returnThemeKey) {
        setThemeCarouselFocusKey(returnThemeKey);
      }
      setEventData({
        ...latestEventData,
        theme_count: isAuthenticated
          ? ([2, 4].includes(requestedCount) ? requestedCount : 4)
          : 2,
        ...(returnThemeKey ? { __focusThemeKey: returnThemeKey, __returnThemeKey: returnThemeKey } : {})
      });

      if (latestEventData.__openFreshSetWorkflow) {
        window.__restoreStep = 'last';
        window.__openFreshSetWorkflow = true;
        setCurrentScreen('input');
        return;
      }
    }
    setCurrentScreen('loading');
  };

  const handleOpenPurchaseModal = ({ tab = 'credits', source = currentView } = {}) => {
    const originScreen = source === 'billing' ? 'input' : currentScreen;
    setInlineBillingHost({
      flow: 'generation',
      screen: originScreen,
      tab: tab,
      eventData: eventData || {}
    });
  };

  const handleOpenBillingFromWizard = (originContext = {}) => {
    const originScreen = originContext?.screen || currentScreen || 'input';
    const shouldReturnToTheme = originScreen === 'carousel' || Boolean(originContext?.returnThemeKey);
    const returnThemeKey = String(
      originContext?.returnThemeKey
      || (shouldReturnToTheme ? themeCarouselFocusKey : '')
      || (shouldReturnToTheme ? eventData?.__focusThemeKey : '')
      || (shouldReturnToTheme ? eventData?.__returnThemeKey : '')
      || ''
    ).trim();
    const returnEventData = returnThemeKey
      ? {
          ...(originContext?.eventData || eventData || {}),
          __focusThemeKey: returnThemeKey,
          __returnThemeKey: returnThemeKey
        }
      : (originContext?.eventData || eventData);

    const inlineBillingFlow = ['fresh_set', 'similar_themes'].includes(originContext?.intent)
      ? 'fresh_set'
      : 'generation';

    // ALWAYS show billing as an overlay/modal inside the wizard
    setShowSaveModal(false);
    setPendingDraftData(null);
    setPendingPostSaveDestination(null);
    setPendingDiscardAction(null);
    setPendingAction(null);
    setPendingSaveData(null);
    setShowLanding(false);
    setForceLanding(false);
    setInlineBillingHost({
      flow: inlineBillingFlow,
      screen: originScreen,
      tab: 'credits',
      returnThemeKey,
      returnToThemesOnClose: shouldReturnToTheme,
      intent: originContext?.intent || 'generation',
      eventData: returnEventData || eventData || {}
    });
  };

  /*
   * PENDING ACTION STATE
   * Stores an action to execute after successful authentication
   */
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingSaveData, setPendingSaveData] = useState(null); // { name: string, active: boolean }

  // Highlight newly saved plan
  const [highlightPlanId, setHighlightPlanId] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (typeof window === 'undefined') return;

    const restoreBillingReturn = async (forcedBillingState = null) => {
      const params = new URLSearchParams(window.location.search);
      const billingState = forcedBillingState || params.get('billing');
      const sessionId = params.get('session_id');
      if (!billingState || !['success', 'cancelled', 'portal'].includes(billingState)) return;

      // Parse raw once near the top of restoreBillingReturn to avoid dashboard flash
      try {
        const raw = sessionStorage.getItem('craftmyparty_billing_return');
        if (raw) {
          const earlySaved = JSON.parse(raw);
          if (earlySaved && ['settings', 'dashboard', 'billing', 'help'].includes(earlySaved.source)) {
            setShowLanding(true);
            setForceLanding(false);
            const view = earlySaved.view || earlySaved.source || 'dashboard';
            setCurrentView(view);
          }
        }
      } catch (err) {
        console.warn('Early billing return parse failed:', err);
      }

      try {
        if (billingState === 'success') {
          try {
            localStorage.setItem('craftmyparty_dev_billing_mode', 'real');
          } catch (_) {}

          if (sessionId && isAuthenticated) {
            try {
              await fetch(`${BACKEND_URL}/api/billing/confirm-session?session_id=${encodeURIComponent(sessionId)}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(await authUtils.getFreshAuthHeader())
                }
              });
            } catch (confirmError) {
              console.warn('Billing confirmation request failed:', confirmError);
            }
          }

          if (isAuthenticated && refreshUser) {
            for (let attempt = 0; attempt < 4; attempt += 1) {
              const refreshed = await refreshUser({ force: true });
              if (refreshed?.billing?.planning_credits > 0 || refreshed?.billing?.subscription_status === 'active') {
                break;
              }
              await new Promise((resolve) => setTimeout(resolve, 900));
            }
          }
        }

        const raw = sessionStorage.getItem('craftmyparty_billing_return');
        if (!raw) {
          sessionStorage.removeItem('craftmyparty_billing_origin');
          replaceAppHistoryRoute({ showLanding: true, currentView: 'dashboard', currentScreen: 'input', planId: null });
          return;
        }
        const saved = JSON.parse(raw);
        if (!saved) {
          sessionStorage.removeItem('craftmyparty_billing_return');
          sessionStorage.removeItem('craftmyparty_billing_origin');
          replaceAppHistoryRoute({ showLanding: true, currentView: 'dashboard', currentScreen: 'input', planId: null });
          return;
        }

        if (['settings', 'dashboard', 'billing', 'help'].includes(saved.source)) {
          setShowLanding(true);
          setForceLanding(false);
          const view = saved.view || saved.source || 'dashboard';
          setCurrentView(view);
          sessionStorage.removeItem('craftmyparty_billing_return');
          sessionStorage.removeItem('craftmyparty_billing_origin');
          replaceAppHistoryRoute({ showLanding: true, currentView: view, currentScreen: 'input', planId: null });
          return;
        }

        if (saved.source !== 'event_input') {
          sessionStorage.removeItem('craftmyparty_billing_return');
          sessionStorage.removeItem('craftmyparty_billing_origin');
          replaceAppHistoryRoute({ showLanding: true, currentView: 'dashboard', currentScreen: 'input', planId: null });
          return;
        }

        setEventData(saved.eventData || null);
        setThemeCarouselFocusKey(saved.eventData?.__focusThemeKey || saved.eventData?.__returnThemeKey || '');
        setThemes(Array.isArray(saved.themes) ? saved.themes : []);
        setCurrentPlanId(saved.planId || null);
        setSelectedTheme(saved.selectedTheme || null);
        setThemePlans(saved.themePlans && typeof saved.themePlans === 'object' ? saved.themePlans : {});
        setThemeSelectionPreferences(null);
        setShoppingItems(Array.isArray(saved.shoppingItems) ? saved.shoppingItems : []);
        setShowLanding(false);
        const screen = saved.screen || 'input';
        setCurrentScreen(screen);
        window.__restoreStep = saved.restoreStep || 'last';
        window.__restoreTime = Date.now();
        setInputResetKey(prev => prev + 1);
        sessionStorage.removeItem('craftmyparty_billing_return');
        sessionStorage.removeItem('craftmyparty_billing_origin');
        replaceAppHistoryRoute({
          showLanding: false,
          currentView: 'dashboard',
          currentScreen: screen,
          planId: saved.planId || null
        });
      } catch (error) {
        console.error('Billing return restore failed:', error);
        sessionStorage.removeItem('craftmyparty_billing_return');
        sessionStorage.removeItem('craftmyparty_billing_origin');
        replaceAppHistoryRoute({ showLanding: true, currentView: 'dashboard', currentScreen: 'input', planId: null });
      }
    };

    const handleBillingRestoreEvent = (event) => {
      restoreBillingReturn(event?.detail?.billingState || 'cancelled');
    };

    window.addEventListener('craftmyparty:restore-billing-return', handleBillingRestoreEvent);
    restoreBillingReturn();
    return () => {
      window.removeEventListener('craftmyparty:restore-billing-return', handleBillingRestoreEvent);
    };
  }, [loading, isAuthenticated, refreshUser]);

  useEffect(() => {
    if (loading || !isAuthenticated || !sessionExpiredLanding) return;

    let restored = false;
    try {
      const raw = localStorage.getItem('craftmyparty_session_recovery');
      if (raw) {
        const recovered = JSON.parse(raw);
        const isFresh = recovered?.savedAt && (Date.now() - recovered.savedAt < 30 * 60 * 1000);
        const matchesUser = recovered && user && recovered.userEmail === user.email;

        if (isFresh && matchesUser) {
          const targetScreen = recovered.currentScreen || 'input';
          const targetView = recovered.currentView || 'dashboard';
          const planId = recovered.currentPlanId || null;

          if (planId) {
            setCurrentPlanId(planId);
            setCurrentScreen(targetScreen);
            setShowLanding(false);
            setCurrentView('dashboard');
            
            replaceAppHistoryRoute({
              showLanding: false,
              currentView: 'dashboard',
              currentScreen: targetScreen,
              planId: planId
            });

            setIsRouteHydrating(true);
            (async () => {
              try {
                const plan = await hydratePlanById(planId);
                if (plan) {
                  handleLoadPlanRef.current?.(plan, targetScreen);
                } else {
                  setShowLanding(true);
                  setCurrentView('dashboard');
                  replaceAppHistoryRoute({ showLanding: true, currentView: 'dashboard', currentScreen: 'input', planId: null });
                }
              } catch (_) {
                setShowLanding(true);
                setCurrentView('dashboard');
                replaceAppHistoryRoute({ showLanding: true, currentView: 'dashboard', currentScreen: 'input', planId: null });
              } finally {
                setIsRouteHydrating(false);
              }
            })();
            restored = true;
          } else if (['settings', 'billing', 'help'].includes(targetView)) {
            setCurrentView(targetView);
            setShowLanding(true);
            setCurrentScreen('input');
            
            replaceAppHistoryRoute({
              showLanding: true,
              currentView: targetView,
              currentScreen: 'input',
              planId: null
            });
            restored = true;
          }
        }
      }
    } catch (err) {
      console.error('Session recovery restoration failed:', err);
    } finally {
      try {
        localStorage.removeItem('craftmyparty_session_recovery');
      } catch (_) {}
    }

    setSessionExpiredLanding(false);
    setIsSessionExpired(false);
    setForceLanding(false);

    if (!restored) {
      setShowLanding(true);
      setCurrentView('dashboard');
      replaceAppHistoryRoute({ showLanding: true, currentView: 'dashboard', currentScreen: 'input', planId: null });
    }
  }, [loading, isAuthenticated, sessionExpiredLanding, user, hydratePlanById]);

  useEffect(() => {
    if (loading || isAuthenticated || !showLanding) return;
    if (!currentPlanId && !eventData && themes.length === 0 && !selectedTheme) return;
    resetAll();
    setShowLanding(true);
    setForceLanding(false);
    setCurrentView('home');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthenticated, showLanding, currentPlanId, eventData, themes.length, selectedTheme]);

  useEffect(() => {
    if (loading || hasRestoredAsyncJobRef.current) return;
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('billing')) return;

    const planJob = getInFlightPlanJob();
    if (planJob?.jobId && planJob?.theme && planJob?.eventData) {
      hasRestoredAsyncJobRef.current = true;
      setShowLanding(false);
      setEventData(planJob.eventData);
      setThemes(Array.isArray(planJob.themes) ? planJob.themes : []);
      setSelectedTheme(planJob.theme);
      setThemePlans(planJob.themePlans && typeof planJob.themePlans === 'object' ? planJob.themePlans : {});
      setThemeSelectionPreferences(planJob.selectionPreferences || null);
      setCurrentPlanId(planJob.currentPlanId || null);
      setCurrentScreen('loadingDetails');
      return;
    }

    const themeJob = getInFlightThemeJob();
    if (themeJob?.jobId && themeJob?.eventData) {
      hasRestoredAsyncJobRef.current = true;
      setShowLanding(false);
      setEventData(themeJob.eventData);
      setThemes([]);
      setSelectedTheme(null);
      setThemeSelectionPreferences(null);
      setCurrentPlanId(themeJob.currentPlanId || null);
      setCurrentScreen('loading');
    }
  }, [loading]);

  const handleSavePlan = (exitAfterSave = false) => {
    let latestEventData = eventData;
    if (currentScreen === 'input' && eventInputScreenRef.current) {
      latestEventData = eventInputScreenRef.current.getCurrentFormData();
      setEventData(latestEventData);
    }

    if (!isAuthenticated) {
      setAuthMode('signup');
      setPendingAction('save');
      setShowAuthModal(true);
      return;
    }

    if (!exitAfterSave && currentScreen === 'input') {
      setPendingDraftData(latestEventData);
      setPendingPostSaveDestination(null);
      setPendingDiscardAction(null);
    }

    setShowSaveModal(true);
  };

  const persistPlanSnapshot = useCallback(async (eventDataOverride = eventData) => {
    if (!isAuthenticated || !currentPlanId) return { ok: false, skipped: true };

    await planUtils.updatePlan(currentPlanId, {
      event_data: stripTransientEventDataKeys(eventDataOverride),
      themes,
      selected_theme: selectedTheme,
      shopping_items: shoppingItems,
      theme_plans: buildThemePlansForSave()
    });
    return { ok: true, skipped: false };
  }, [
    isAuthenticated,
    currentPlanId,
    eventData,
    themes,
    selectedTheme,
    shoppingItems,
    buildThemePlansForSave
  ]);

  // Auto-save shopping items / theme-plan changes to the loaded plan after a
  // short debounce. Only runs when (a) a plan is loaded, (b) the content has
  // actually been modified (planContentDirtyRef.current is set by
  // markPlanContentDirty). This prevents the regression where shortlisted
  // links added on the Plan screen vanished after a fresh session because
  // the user never explicitly pressed Save.
  useEffect(() => {
    if (!isAuthenticated || !currentPlanId) return;
    if (!planContentDirtyRef.current) return;
    if (currentScreen !== 'plan' && currentScreen !== 'shopping-list') return;
    const timeoutId = setTimeout(async () => {
      try {
        await persistPlanSnapshot();
        clearUnsavedChanges();
        setCanSave(false);
        planContentDirtyRef.current = false;
      } catch (err) {
        // Auto-save errors are silent — user still has explicit Save UI.
        console.warn('Auto-save failed:', err?.message || err);
      }
    }, 800);
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shoppingItems, themePlans, isAuthenticated, currentPlanId, currentScreen]);

  const handlePlanCoreDetailsSave = async (updates) => {
    const nextEventData = { ...(eventData || {}), ...updates };
    setEventData(nextEventData);

    try {
      const result = await persistPlanSnapshot(nextEventData);
      if (result.ok) {
        if (planContentDirtyRef.current) {
          markUnsavedChanges();
          setCanSave(true);
        } else {
          clearUnsavedChanges();
          setCanSave(false);
        }
      } else {
        markUnsavedChanges();
        setCanSave(true);
      }
    } catch (error) {
      console.error('Failed to save plan details:', error);
      markUnsavedChanges();
      setCanSave(true);
    }
  };

  const handlePlanCoreDetailsReEstimate = (updates) => {
    const nextEventData = {
      ...(eventData || {}),
      ...updates,
      __recalculateCurrentPlan: true,
      __focusThemeKey: getThemePlanKey(selectedTheme),
      __returnThemeKey: getThemePlanKey(selectedTheme)
    };
    setEventData(nextEventData);
    setCurrentScreen('loadingDetails');
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);

    // Explicit pending action (e.g. from Save button)
    if (pendingAction === 'save') {
      if (pendingSaveData && pendingSaveData.active && pendingSaveData.name) {
        // Auto-save logic
        try {
          const planDataToSave = {
            event_data: stripTransientEventDataKeys({
              ...eventData,
              plan_name: pendingSaveData.name
            }),
            themes,
            selected_theme: selectedTheme,
            shopping_items: shoppingItems,
            theme_plans: buildThemePlansForSave()
          };

          let result;
          if (currentPlanId) {
            result = await planUtils.updatePlan(currentPlanId, planDataToSave);
          } else {
            result = await planUtils.createPlan(planDataToSave);
          }
          const savedId = result?.plan_id || currentPlanId;

          if (pendingPostSaveDestination) {
            const destination = pendingPostSaveDestination;
            setPendingPostSaveDestination(null);
            navigateAfterWizardExit(destination, savedId);
          } else {
            // Success: Reset and Redirect to Dashboard
            resetAll();
            setShowSaveModal(false);
            setShowLanding(true);
            if (savedId) setHighlightPlanId(savedId);
            window.__reloadDashboard = true;
            setCurrentView('dashboard');
          }

          setPendingAction(null);
          setPendingSaveData(null);
          return;
        } catch (err) {
          console.error("Auto-save failed", err);
          // Fallback to manual save if auto-save fails
        }
      }

      setPendingAction(null);
      setTimeout(() => setShowSaveModal(true), 300);
      return;
    }

    // General unsaved changes check (user signed in via menu)
    if (checkUnsavedChanges()) {
      setTimeout(() => setShowSaveModal(true), 300);
    }
  };

  const handleDirectPlanConfirm = () => {
    setShowDirectPlanModal(false);
    // Logic to jump to plan/theme loaded
  };

  const applyShoppingItemsUpdate = (items) => {
    setShoppingItems(items);
    const themePlanKey = getThemePlanKey(selectedTheme);
    if (themePlanKey && selectedTheme) {
      setThemePlans(prev => ({
        ...prev,
        [themePlanKey]: {
          ...(prev?.[themePlanKey] || {}),
          selected_theme: selectedTheme,
          shopping_items: Array.isArray(items) ? items : [],
          updated_at: new Date().toISOString()
        }
      }));
    }
    // Mark dirty so the auto-save effect picks up shopping list changes —
    // including newly-added shortlistedLinks — without the user having to
    // press Save. Without this, links/purchased toggles disappeared after a
    // refresh because they only lived in component state.
    markPlanContentDirty();
  };

  // When the user opens a sibling theme plan, items that exist in BOTH plans
  // (matched by catalog_key or name+category) inherit any shortlistedLinks the
  // other plan has accumulated. Same-event plans share menu/decor items so
  // this saves users from re-pasting product links per theme.
  const itemsMatchForLinkSync = (a, b) => {
    if (!a || !b) return false;
    if (a.catalog_key && b.catalog_key) {
      return String(a.catalog_key) === String(b.catalog_key);
    }
    const nameA = String(a.name || '').trim().toLowerCase();
    const nameB = String(b.name || '').trim().toLowerCase();
    if (!nameA || nameA !== nameB) return false;
    const catA = String(a.category || '').trim().toLowerCase();
    const catB = String(b.category || '').trim().toLowerCase();
    if (!catA || !catB) return true;
    return catA === catB;
  };
  const dedupeLinks = (links) => {
    const seen = new Set();
    const out = [];
    (Array.isArray(links) ? links : []).forEach((link) => {
      const url = String(link?.url || '').trim();
      if (!url || seen.has(url)) return;
      seen.add(url);
      out.push({ url, label: String(link?.label || '').trim() || 'Link' });
    });
    return out;
  };
  const propagateItemLinksToSiblings = (updatedItem, newLinks) => {
    if (!updatedItem) return;
    const linkSet = dedupeLinks(newLinks);
    setThemePlans(prev => {
      const next = { ...(prev || {}) };
      const currentThemeKey = getThemePlanKey(selectedTheme);
      Object.keys(next).forEach((planKey) => {
        if (planKey === currentThemeKey) return; // current plan is already updated by setItems
        const plan = next[planKey];
        if (!plan || !Array.isArray(plan.shopping_items) || plan.shopping_items.length === 0) return;
        let changed = false;
        const nextItems = plan.shopping_items.map((it) => {
          if (!itemsMatchForLinkSync(it, updatedItem)) return it;
          const merged = dedupeLinks([...(it.shortlistedLinks || []), ...linkSet]);
          if (merged.length === (it.shortlistedLinks?.length || 0)
              && merged.every((l, i) => l.url === it.shortlistedLinks?.[i]?.url)) {
            return it;
          }
          changed = true;
          return { ...it, shortlistedLinks: merged };
        });
        if (changed) {
          next[planKey] = {
            ...plan,
            shopping_items: nextItems,
            updated_at: new Date().toISOString()
          };
        }
      });
      return next;
    });
  };
  const mergeLinksFromSiblingPlans = (incomingItems, planKeyToOpen) => {
    const list = Array.isArray(incomingItems) ? incomingItems : [];
    if (list.length === 0) return list;
    const siblingPlans = Object.entries(themePlans || {}).filter(([key]) => key !== planKeyToOpen);
    if (siblingPlans.length === 0) return list;
    return list.map((item) => {
      const siblingLinks = [];
      siblingPlans.forEach(([, plan]) => {
        const matched = (plan?.shopping_items || []).find((other) => itemsMatchForLinkSync(other, item));
        if (matched && Array.isArray(matched.shortlistedLinks)) {
          siblingLinks.push(...matched.shortlistedLinks);
        }
      });
      if (siblingLinks.length === 0) return item;
      const merged = dedupeLinks([...(item.shortlistedLinks || []), ...siblingLinks]);
      if (merged.length === (item.shortlistedLinks?.length || 0)) return item;
      return { ...item, shortlistedLinks: merged };
    });
  };

  // Toggle the current plan's is_favorite flag via the lightweight PATCH
  // endpoint. Optimistic — flip locally and roll back on error.
  const handleToggleCurrentPlanFavorite = useCallback(async () => {
    if (!currentPlanId) return;
    const next = !currentPlanIsFavorite;
    setCurrentPlanIsFavorite(next);
    try {
      await planUtils.updatePlanFavorite(currentPlanId, { isFavorite: next });
      try { window.__reloadDashboard = true; } catch (_) {}
    } catch (err) {
      console.error('Failed to toggle plan favorite:', err);
      setCurrentPlanIsFavorite(!next);
    }
  }, [currentPlanId, currentPlanIsFavorite]);

  // Toggle a specific theme's favorite status for the current plan. The
  // canonical store is favorite_theme_keys on the plan; we also dispatch the
  // PATCH endpoint to persist.
  const handleToggleThemeFavorite = useCallback(async (themeKey) => {
    if (!currentPlanId || !themeKey) return;
    const trimmed = String(themeKey).trim();
    if (!trimmed) return;
    setCurrentPlanFavoriteThemeKeys(prev => {
      const has = prev.includes(trimmed);
      const next = has ? prev.filter(k => k !== trimmed) : [...prev, trimmed];
      
      // Fire-and-forget the API call; revert on error
      planUtils.updatePlanFavorite(currentPlanId, { favoriteThemeKeys: next })
        .catch(err => {
          console.error('Failed to toggle theme favorite:', err);
          setCurrentPlanFavoriteThemeKeys(prev);
        });
        
      return next;
    });
  }, [currentPlanId]);

  const handleLoadPlan = (plan, targetScreen = null) => {
    const loadedThemePlans = plan.theme_plans && typeof plan.theme_plans === 'object'
      ? { ...plan.theme_plans }
      : {};
    const legacyThemeKey = getThemePlanKey(plan.selected_theme);
    if (legacyThemeKey && !loadedThemePlans[legacyThemeKey]) {
      loadedThemePlans[legacyThemeKey] = {
        selected_theme: plan.selected_theme,
        shopping_items: Array.isArray(plan.shopping_items) ? plan.shopping_items : [],
        updated_at: plan.updated_at
      };
    }
    const latestThemePlan = Object.values(loadedThemePlans)
      .filter(snapshot => snapshot?.selected_theme)
      .sort((a, b) => new Date(b?.updated_at || 0) - new Date(a?.updated_at || 0))[0];
    const loadedSelectedTheme = plan.selected_theme || latestThemePlan?.selected_theme || null;
    const loadedShoppingItems = Array.isArray(plan.shopping_items) && plan.shopping_items.length > 0
      ? plan.shopping_items
      : (Array.isArray(latestThemePlan?.shopping_items) ? latestThemePlan.shopping_items : []);

    const sanitizedEventData = stripTransientEventDataKeys(
      ensurePlanEstimateCore(plan.event_data || {})
    );
    setEventData(sanitizedEventData);
    setThemes(plan.themes || []);
    setSelectedTheme(loadedSelectedTheme);
    setThemeCarouselFocusKey(getThemePlanKey(loadedSelectedTheme) || '');
    setThemePlans(loadedThemePlans);
    setThemeSelectionPreferences(null);
    setShoppingItems(loadedShoppingItems);
    setCurrentPlanId(plan.plan_id);
    setCurrentPlanIsFavorite(Boolean(plan.is_favorite));
    setCurrentPlanFavoriteThemeKeys(Array.isArray(plan.favorite_theme_keys) ? plan.favorite_theme_keys : []);
    clearUnsavedChanges();
    setCanSave(false);
    planContentDirtyRef.current = false;
    setShowThemeOptionsFromDetails(false);

    // 2. Determine correct screen based on progress
    let resolvedScreen = targetScreen;

    const focusSel = plan.event_data?.focus_selection || [];
    const isThemeBased = !focusSel || focusSel.length === 0 || focusSel.includes('everything') || focusSel.includes('theme_ideas');

    if (!isThemeBased) {
      setCurrentScreen('plan');
    } else {
      if (resolvedScreen === 'plan' && !loadedSelectedTheme) {
        resolvedScreen = (!plan.themes || plan.themes.length === 0) ? 'plan' : 'carousel';
      }

      if (resolvedScreen === 'carousel' && (!plan.themes || plan.themes.length === 0)) {
        resolvedScreen = 'plan';
      }

      if (resolvedScreen) {
        setCurrentScreen(resolvedScreen);
        if (resolvedScreen === 'input') {
          const savedDraftStep = Number(plan.event_data?.draft_step_index);
          window.__restoreStep = (Number.isFinite(savedDraftStep) && savedDraftStep > 0)
            ? savedDraftStep
            : 'firstIncomplete';
          window.__restoreTime = Date.now();
          setInputResetKey(prev => prev + 1);
        }
      } else if (!plan.themes || plan.themes.length === 0) {
        // DRAFT -> Plan Screen (so it doesn't silently redirect, and shows the incomplete state)
        setCurrentScreen('plan');
      } else if (!loadedSelectedTheme) {
        // ACTIVE (Themes generated but not selected) -> Carousel
        setCurrentScreen('carousel');
      } else {
        // COMPLETED (Theme selected) -> Plan
        setCurrentScreen('plan');
      }
    }

    setShowLanding(false);
  };
  handleLoadPlanRef.current = handleLoadPlan;

  const handleViewThemePlan = (themeOrKey) => {
    const outgoingKey = getThemePlanKey(selectedTheme);
    if (outgoingKey && currentScreen === 'plan') {
      planTabSessionRef.current[outgoingKey] = planActiveTab;
    }
    const themePlanKey = typeof themeOrKey === 'string'
      ? String(themeOrKey).trim()
      : getThemePlanKey(themeOrKey);
    const resolvedTheme = typeof themeOrKey === 'string'
      ? themes.find((candidate) => getThemePlanKey(candidate) === themePlanKey) || null
      : themeOrKey;
    const restorePlanTab = (key) => {
      if (!key) return;
      const saved = planTabSessionRef.current[key];
      setPlanActiveTab(normalizePlanTabForLayout(saved ?? 'overview'));
    };
    if (themePlanKey) {
      setThemeCarouselFocusKey(themePlanKey);
      setEventData(prev => ({
        ...(prev || eventData || {}),
        __focusThemeKey: themePlanKey,
        __returnThemeKey: themePlanKey
      }));
    }
    const savedThemePlan = themePlanKey ? themePlans?.[themePlanKey] : null;
    const selectedThemePlanKey = getThemePlanKey(selectedTheme);

    if (savedThemePlan) {
      setSelectedTheme(savedThemePlan.selected_theme || resolvedTheme || selectedTheme);
      setThemeSelectionPreferences(null);
      const incomingItems = Array.isArray(savedThemePlan.shopping_items) ? savedThemePlan.shopping_items : [];
      const mergedItems = mergeLinksFromSiblingPlans(incomingItems, themePlanKey);
      setShoppingItems(mergedItems);
      // If sibling links were added, persist them back into themePlans so the
      // newly-merged links are remembered.
      if (themePlanKey && mergedItems !== incomingItems) {
        setThemePlans(prev => ({
          ...prev,
          [themePlanKey]: {
            ...(prev?.[themePlanKey] || {}),
            selected_theme: savedThemePlan.selected_theme || resolvedTheme || selectedTheme,
            shopping_items: mergedItems,
            updated_at: new Date().toISOString()
          }
        }));
      }
      restorePlanTab(themePlanKey);
      setShowThemeOptionsFromDetails(false);
      setCurrentScreen('plan');
      setShowLanding(false);
      return;
    }

    if (
      themePlanKey
      && selectedThemePlanKey
      && themePlanKey === selectedThemePlanKey
      && shoppingItems.length > 0
    ) {
      setSelectedTheme(resolvedTheme || selectedTheme);
      setThemeSelectionPreferences(null);
      restorePlanTab(themePlanKey);
      setShowThemeOptionsFromDetails(false);
      setCurrentScreen('plan');
      setShowLanding(false);
      return;
    }

    if (resolvedTheme) {
      handleThemeSelect(resolvedTheme);
    }
  };

  const handleSaveDraft = (data) => {
    // If not authenticated, open auth modal first
    if (!isAuthenticated) {
      setAuthMode('signin');
      setPendingAction('save');
      setPendingSaveData({ active: true, name: data?.event_type || 'Untitled Draft' });
      setShowAuthModal(true);
      return;
    }
    const sanitizedDraftData = stripTransientEventDataKeys(ensurePlanEstimateCore(data || {}));
    pendingDraftDataRef.current = sanitizedDraftData;
    setPendingDraftData(sanitizedDraftData);
    setEventData(sanitizedDraftData);
    setShowSaveModal(true);
  };

  // Nav Logic
  const nav = {
    maxStepIndex: selectedTheme ? 2 : themes.length > 0 ? 1 : 0,
    completedSteps: {},
    onStepClick: (key) => {
      // If leaving input screen, sync the latest form data to global state
      if (currentScreen === 'input' && eventInputScreenRef.current) {
        const latestData = eventInputScreenRef.current.getCurrentFormData();
        setEventData(prev => ({ ...prev, ...latestData }));
      }

      setShowThemeOptionsFromDetails(false);

      if (key === 'input') {
        window.__restoreStep = themes.length > 0 || eventData ? 'last' : null;
        navigateToInput();
      }
      if (key === 'themes') {
        if (themeCarouselFocusKey) {
          setEventData(prev => ({
            ...(prev || eventData || {}),
            __focusThemeKey: themeCarouselFocusKey,
            __returnThemeKey: themeCarouselFocusKey
          }));
        }
        setCurrentScreen('carousel');
      }
      if (key === 'plan') setCurrentScreen('plan');
    },
    onBack: () => {
      setShowThemeOptionsFromDetails(false);
      const focusSel = eventData?.focus_selection || [];
      const isThemeBased = !focusSel || focusSel.length === 0 || focusSel.includes('everything') || focusSel.includes('theme_ideas');
      if (currentScreen === 'carousel') {
        navigateToInput();
      } else if (currentScreen === 'plan') {
        if (isThemeBased) {
          setCurrentScreen('carousel');
        } else {
          navigateToInput();
        }
      }
    },
    onNext: () => { }, // Handled by screens
    nextLabel: 'Next',
    showCancel: false,
    onCancel: handleGoToHome,
    shouldShowStartOver: true
  };

  const getAppTopBarProps = () => {
    const baseProps = {
      onBillingClick: () => handleOpenPurchaseModal({ tab: 'credits' })
    };
    if (['dashboard', 'plans', 'shared'].includes(currentView)) return baseProps;
    const backView = previousView && previousView !== currentView ? previousView : 'dashboard';
    return {
      ...baseProps,
      breadcrumbs: [
        {
          label: `Back to ${viewLabels[backView] || 'Dashboard'}`,
          onClick: () => navigateAppView(backView)
        },
        { label: viewLabels[currentView] || 'Page' }
      ],
      searchPlaceholder: currentView === 'help' ? 'Search help...' : 'Search plans...'
    };
  };

  // --- RENDER ---

  const renderGlobalConfirmationModal = () => (
    <ConfirmationModal
      {...confirmationModal}
      onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
    />
  );

  const showAuthBootScreen = loading && !user && !initialRoute.optimisticShell && !initialRoute.optimisticWizard;
  const showInputHydrationScreen = currentScreen === 'input' && Boolean(isRouteHydrating || bootRestore);
  // True only while the plan route is hydrating AND selectedTheme has not yet been loaded.
  // Prevents <Plan> from rendering with empty data (which would show the incomplete-plan fallback).
  const isPlanRouteLoading = currentScreen === 'plan' && !selectedTheme && Boolean(isRouteHydrating || bootRestore);

  if (showAuthBootScreen) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  const pathname = currentPathname;

  const isFaqPage =
    ['faq'].includes(currentView) ||
    ['/faq'].includes(pathname);

  if (isFaqPage) {
    return <FAQPage />;
  }

  const isPrivacyPage =
    ['privacy', 'privacy-policy'].includes(currentView) ||
    ['/privacy', '/privacy-policy'].includes(pathname);

  const isTermsPage =
    ['terms', 'terms-of-service'].includes(currentView) ||
    ['/terms', '/terms-of-service'].includes(pathname);

  if (isPrivacyPage) {
    return <PrivacyPolicy />;
  }

  if (isTermsPage) {
    return <TermsOfService />;
  }

  const isSharePage =
    pathname.startsWith('/share/') ||
    pathname.startsWith('/shared/') ||
    pathname.startsWith('/p/');

  if (isSharePage) {
    const shareToken = pathname.replace(/^\/(share|shared|p)\//, '').split('/')[0].split('?')[0];
    return (
      <PublicGuestShowcase
        shareToken={shareToken}
        onStartOwnPlan={() => {
          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', '/');
            setCurrentPathname('/');
            setShowLanding(false);
          }
        }}
      />
    );
  }

  const isOccasionsPage = pathname.startsWith('/occasions');
  if (isOccasionsPage) {
    const slug = pathname.replace(/^\/occasions\/?/, '').split('/')[0].split('?')[0];
    return (
      <OccasionLandingPage
        slug={slug}
        onSelectOccasion={(preset) => {
          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', '/');
            setCurrentPathname('/');
          }
          startNewPlanFlow();
          if (preset) {
            setEventData((prev) => ({
              ...(prev || {}),
              occasion: preset.occasion,
              event_type: preset.occasion.includes('Birthday') ? 'Birthday' : preset.occasion.includes('Shower') ? 'Baby Shower' : 'Dinner Party',
              theme_input: preset.themeName,
              budget: preset.defaultBudget,
              guest_count: preset.defaultGuests
            }));
          }
        }}
        onNavigateHome={() => {
          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', '/home');
            setCurrentPathname('/home');
            setShowLanding(true);
            setCurrentView('home');
          }
        }}
      />
    );
  }

  const isPublicHome = pathname === '/home';
  if (isPublicHome) {
    return (
      <LandingPage
        onEnterApp={() => {
          if (isAuthenticated) {
            authIntentRef.current = null;
            writeStoredAuthIntent(null);
            setCurrentView('dashboard');
            window.location.href = '/';
          } else {
            setShowLanding(false);
            if (typeof window !== 'undefined') {
              window.history.pushState({}, '', '/');
              setCurrentPathname('/');
            }
          }
        }}
        isAuthenticated={isAuthenticated}
        user={user}
        onGoToDashboard={() => {
          authIntentRef.current = null;
          writeStoredAuthIntent(null);
          setCurrentView('dashboard');
          window.location.href = '/';
        }}
        onShowPrivacy={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
        onShowTerms={() => window.open('/terms', '_blank', 'noopener,noreferrer')}
        onShowFaq={() => window.open('/faq', '_blank', 'noopener,noreferrer')}
        onAuthIntent={setAuthIntent}
        onSignOut={performSignOut}
        onStartPlanning={handleStartPlanning}
        onGoToBilling={handleGoToBilling}
      />
    );
  }

  if (sessionExpiredLanding) {
    return (
      <LandingPage
        onEnterApp={() => {
          setSessionExpiredLanding(false);
          setShowLanding(false);
          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', '/');
            setCurrentPathname('/');
          }
        }}
        isAuthenticated={false}
        onShowPrivacy={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
        onShowTerms={() => window.open('/terms', '_blank', 'noopener,noreferrer')}
        onShowFaq={() => window.open('/faq', '_blank', 'noopener,noreferrer')}
        onAuthIntent={setAuthIntent}
        onSignOut={performSignOut}
        onStartPlanning={handleStartPlanning}
        onGoToBilling={handleGoToBilling}
      />
    );
  }

  if (showLanding && !isAuthenticated) {
    return (
      <LandingPage
        onEnterApp={() => {
          setShowLanding(false);
          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', '/');
            setCurrentPathname('/');
          }
        }}
        isAuthenticated={false}
        onShowPrivacy={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
        onShowTerms={() => window.open('/terms', '_blank', 'noopener,noreferrer')}
        onShowFaq={() => window.open('/faq', '_blank', 'noopener,noreferrer')}
        onAuthIntent={setAuthIntent}
        onSignOut={performSignOut}
        onStartPlanning={handleStartPlanning}
        onGoToBilling={handleGoToBilling}
      />
    );
  }

  if ((forceLanding || currentView === 'home') && isAuthenticated) {
    return (
      <LandingPage
        onEnterApp={() => {
          authIntentRef.current = null;
          writeStoredAuthIntent(null);
          setForceLanding(false);
          setCurrentView('dashboard');
        }}
        isAuthenticated={isAuthenticated}
        user={user}
        onGoToDashboard={() => {
          authIntentRef.current = null;
          writeStoredAuthIntent(null);
          setForceLanding(false);
          setCurrentView('dashboard');
        }}
        onShowPrivacy={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
        onShowTerms={() => window.open('/terms', '_blank', 'noopener,noreferrer')}
        onShowFaq={() => window.open('/faq', '_blank', 'noopener,noreferrer')}
        onAuthIntent={setAuthIntent}
        onSignOut={performSignOut}
        onStartPlanning={handleStartPlanning}
        onGoToBilling={handleGoToBilling}
      />
    );
  }

  // Determine view based on auth
  if (showLanding && isAuthenticated) {
    return (
      <>
        <AppLayout
          currentView={currentView}
          navigationKey={navigationKey}
          onNavigate={navigateAppView}
          onNewPlan={startNewPlanFlow}
          topBarProps={getAppTopBarProps()}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          onAutoCollapse={() => setSidebarCollapsed(true)}
          onShowPrivacy={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
          onShowTerms={() => window.open('/terms', '_blank', 'noopener,noreferrer')}
          onSignOut={handleSignOut}
          user={user}
        >
          {(currentView === 'dashboard' || currentView === 'plans' || currentView === 'shared') && (
            <Dashboard
              currentView={currentView}
              onStartNewPlan={startNewPlanFlow}
              onLoadPlan={handleLoadPlan}
              onNavigate={navigateAppView}
              highlightPlanId={highlightPlanId}
              onShowPrivacy={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
              onShowTerms={() => window.open('/terms', '_blank', 'noopener,noreferrer')}
            />
          )}
          {currentView === 'settings' && <SettingsPage />}
          {currentView === 'billing' && (
            <BillingPage
              onOpenPurchaseModal={handleOpenPurchaseModal}
            />
          )}
          {currentView === 'help' && <HelpCenterPage />}
          {/* Other views */}
        </AppLayout>
        {renderGlobalConfirmationModal()}
        {inlineBillingHost && (
          <EventInputScreen
            billingOverlayOnly
            inlineBillingRequest={inlineBillingHost}
            initialEventData={inlineBillingHost.eventData}
            existingThemes={themes.length > 0 ? themes : null}
            selectedTheme={selectedTheme}
            shoppingItems={shoppingItems}
            themePlansForReturn={themePlans}
            currentPlanId={currentPlanId}
            isAuthenticated={isAuthenticated}
            user={user}
            onBillingOverlayClose={() => {
              setInlineBillingHost(null);
            }}
            onSubmit={() => {}}
          />
        )}
      </>
    );
  }

  // WIZARD FLOW (Creating Plan)
  // FIXED: Wrap in AppLayout to ensure Sidebar visibility
  const activeDraftData = pendingDraftDataRef.current || pendingDraftData || eventData;

  return (
    <AppLayout
      currentView={currentScreen}
      navigationKey={navigationKey}
      onNavigate={(view) => {
        if (view === 'signin') {
          setAuthMode('signin');
          setShowAuthModal(true);
        } else if (view === 'signup') {
          setAuthMode('signup');
          setShowAuthModal(true);
        } else if (['dashboard', 'plans', 'shared', 'settings', 'billing', 'help', 'home'].includes(view)) {
          exitWizardToView(view);
        } else {
          handleGoToHome();
        }
      }}
      // Part D: Privacy/Terms open in a new tab — no workflow navigation needed.
      onShowPrivacy={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
      onShowTerms={() => window.open('/terms', '_blank', 'noopener,noreferrer')}
      onNewPlan={() => { }}
      isCreatingPlan={true}
      showTopBar={false} // Use TopStepperNav instead of standard top bar
      isCollapsed={sidebarCollapsed}
      onToggleCollapse={toggleSidebar}
      onAutoCollapse={() => setSidebarCollapsed(true)}
      user={isAuthenticated ? user : null}
      // Part C: Guard sign-out through the save/discard modal when in workflow.
      onSignOut={() => exitWizardToView('signout')}
    >
      {/* Sticky Stepper */}
      <div className="app-nav-wrapper relative z-20">
        <TopStepperNav
          eventData={eventData}
          currentScreen={currentScreen}
          maxStepIndex={nav.maxStepIndex}
          conceptsExist={themes && themes.length > 0}
          planExists={selectedTheme !== null || (shoppingItems && shoppingItems.length > 0)}
          completedSteps={nav.completedSteps}
          onStepClick={nav.onStepClick}
          onBack={nav.onBack}
          onNext={nav.onNext}
          nextLabel={nav.nextLabel}
          showCancel={nav.showCancel}
          onCancel={nav.onCancel}
          onStartOver={handleStartOver}
          shouldShowStartOver={nav.shouldShowStartOver}
          showMobileNav={true}
          onMenuToggle={null}
          onHomeClick={() => handleGoToHome()}
          isAuthenticated={isAuthenticated}
          onMyPlans={() => exitWizardToView('dashboard')}
          onSavePlan={handleSavePlan}
          onBillingClick={handleOpenBillingFromWizard}
          onSharePlan={() => setShowShareModal(true)}
          selectedTheme={selectedTheme}
          showHeader={true}

          saveEnabled={canSave}
          isCollapsed={sidebarCollapsed}
        />
      </div>

      {/* Content */}
      <div
        ref={swipeAreaRef}
        className="app-content-wrapper container mx-auto px-4 pb-6 md:pb-12 pt-0 md:pt-4 relative"
        style={{
          touchAction: 'pan-y'
        }}
      >
        {showInputHydrationScreen && (
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-gray-500">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
            <p className="text-sm font-medium">Loading event details…</p>
          </div>
        )}

        {currentScreen === 'input' && !showInputHydrationScreen && (
          <>
            <EventInputScreen
              ref={eventInputScreenRef}
              key={`input-${inputResetKey}`}
              onSubmit={handleEventSubmit}
              initialEventData={eventData}
              currentPlanId={currentPlanId}
              selectedTheme={selectedTheme}
              shoppingItems={shoppingItems}
              themePlans={themePlans}
              themePlansForReturn={themePlans}
              initialStep={window.__restoreStep || null}
              onRegisterSwipeHandlers={setInputSwipeHandlers}
              existingThemes={themes.length > 0 ? themes : null}
              saveSuccessToken={saveSuccessToken}
              onFocusChange={(newFocus) => {
                let latestData = {};
                if (eventInputScreenRef.current) {
                  try {
                    const snapshot = eventInputScreenRef.current.getCurrentFormData() || {};
                    latestData = {
                      event_type: snapshot.event_type,
                      custom_event_type: snapshot.custom_event_type,
                      event_specifics: snapshot.event_specifics,
                      budget: snapshot.budget,
                      guest_count: snapshot.guest_count,
                      zip_code: snapshot.zip_code,
                      date: snapshot.date
                    };
                  } catch (e) {
                    console.warn('Unable to retrieve form data snapshot on focus change:', e);
                  }
                }
                setEventData(prev => ({
                  ...(prev || {}),
                  ...latestData,
                  focus_selection: newFocus
                }));
              }}
              onNavigateToThemes={() => {
                // Sync latest form data before going to carousel so budget/guests etc. are current
                if (eventInputScreenRef.current) {
                  const latestData = eventInputScreenRef.current.getCurrentFormData();
                  const focusKey = themeCarouselFocusKey
                    || latestData?.__focusThemeKey
                    || latestData?.__returnThemeKey;
                  setEventData(prev => ({
                    ...prev,
                    ...latestData,
                    ...(focusKey ? { __focusThemeKey: focusKey, __returnThemeKey: focusKey } : {})
                  }));
                } else if (themeCarouselFocusKey) {
                  setEventData(prev => ({
                    ...(prev || eventData || {}),
                    __focusThemeKey: themeCarouselFocusKey,
                    __returnThemeKey: themeCarouselFocusKey
                  }));
                }
                const focusSel = eventData?.focus_selection || [];
                const isThemeBased = !focusSel || focusSel.length === 0 || focusSel.includes('everything') || focusSel.includes('theme_ideas');
                if (!isThemeBased) {
                  setCurrentScreen('plan');
                } else if (selectedTheme) {
                  setShowThemeOptionsFromDetails(true);
                } else {
                  setCurrentScreen('carousel');
                }
              }}
              onRegenerateThemes={handleRegenerateThemes}
              goToStep16Key={goToStep16Key}
              onSaveDraft={isAuthenticated ? async (draftData) => {
                await handleSaveDraft(draftData);
                setEventData(draftData);
              } : undefined}
              onRequestAuth={(mode = 'signup') => {
                setAuthMode(mode);
                setShowAuthModal(true);
              }}
              isAuthenticated={isAuthenticated}
              user={user}
              onDirtyChange={setCanSave}
              sidebarCollapsed={sidebarCollapsed}
              onSavePlan={handleSavePlan}
            />

            {showThemeOptionsFromDetails && (
              <div className="fixed inset-0 z-[100] flex flex-col bg-gray-50 overflow-y-auto pointer-events-auto">
                <div className="w-full bg-white border-b border-gray-200 sticky top-0 z-30 px-4 py-3 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowThemeOptionsFromDetails(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
                      aria-label="Back to Details"
                    >
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="font-bold text-gray-900 text-lg">Theme Options</span>
                  </div>
                  <button
                    onClick={() => setShowThemeOptionsFromDetails(false)}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition focus:outline-none"
                  >
                    Close
                  </button>
                </div>
                <div className="flex-1 pb-12">
                  <ThemeCarousel
                    themes={themes}
                    onThemeSelect={handleThemeSelect}
                    onRegenerate={handleRegenerateThemes}
                    onBackToInput={() => setShowThemeOptionsFromDetails(false)}
                    eventData={eventData}
                    isAuthenticated={isAuthenticated}
                    onRequestAuth={(mode = 'signup') => {
                      setAuthMode(mode);
                      setShowAuthModal(true);
                    }}
                    onBuyCredits={handleOpenBillingFromWizard}
                    selectedTheme={selectedTheme}
                    themePlans={themePlans}
                    onViewThemePlan={handleViewThemePlan}
                    hasShoppingItems={shoppingItems.length > 0}
                    onEditTheme={handleOpenThemeEdit}
                    sidebarCollapsed={sidebarCollapsed}
                    onThemeFocusChange={setThemeCarouselFocusKey}
                    favoriteThemeKeys={currentPlanFavoriteThemeKeys}
                    onToggleThemeFavorite={currentPlanId ? handleToggleThemeFavorite : null}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {currentScreen === 'loading' && (
          <LoadingScreen
            eventData={eventData}
            currentPlanId={currentPlanId}
            onThemesGenerated={handleThemesGenerated}
            onError={handleLoadingError}
          />
        )}

        {currentScreen === 'carousel' && (isRouteHydrating || bootRestore) && themes.length === 0 && (
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-gray-500">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
            <p className="text-sm font-medium">Loading themes…</p>
          </div>
        )}

        {currentScreen === 'carousel' && !((isRouteHydrating || bootRestore) && themes.length === 0) && (
          <ThemeCarousel
            themes={themes}
            onThemeSelect={handleThemeSelect}
            onRegenerate={handleRegenerateThemes}
            onBackToInput={navigateToInput}
            eventData={eventData}
            isAuthenticated={isAuthenticated}
            onRequestAuth={(mode = 'signup') => {
              setAuthMode(mode);
              setShowAuthModal(true);
            }}
            onBuyCredits={handleOpenBillingFromWizard}
            selectedTheme={selectedTheme}
            themePlans={themePlans}
            onViewThemePlan={handleViewThemePlan}
            hasShoppingItems={shoppingItems.length > 0}
            onEditTheme={handleOpenThemeEdit}
            sidebarCollapsed={sidebarCollapsed}
            onThemeFocusChange={setThemeCarouselFocusKey}
            favoriteThemeKeys={currentPlanFavoriteThemeKeys}
            onToggleThemeFavorite={currentPlanId ? handleToggleThemeFavorite : null}
          />
        )}

        {currentScreen === 'loadingDetails' && selectedTheme && (
          <ThemeDetailsLoading
            theme={selectedTheme}
            themes={themes}
            eventData={eventData}
            selectionPreferences={themeSelectionPreferences}
            themePlans={themePlans}
            currentPlanId={currentPlanId}
            onDetailsLoaded={handleDetailsLoaded}
            onCancel={handleBackToThemes}
          />
        )}

        {isPlanRouteLoading && (
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-gray-500">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
            <p className="text-sm font-medium">Loading your plan…</p>
          </div>
        )}

        {currentScreen === 'plan' && !isPlanRouteLoading && (
          <Plan
            theme={selectedTheme}
            eventData={eventData}
            items={shoppingItems}
            setItems={applyShoppingItemsUpdate}
            onBackToThemes={handleBackToThemes}
            onTryDifferent={handleBackToThemes}
            onThemeRefined={handleThemeRefined}
            onShare={() => setShowShareModal(true)}
            onSaveCoreEventDetails={handlePlanCoreDetailsSave}
            onReEstimatePlan={handlePlanCoreDetailsReEstimate}
            onSavePlan={handleSavePlan}
            savePlanEnabled={canSave}
            planId={currentPlanId}
            themePlans={themePlans}
            onViewThemePlan={handleViewThemePlan}
            onItemLinksChanged={propagateItemLinksToSiblings}
            isFavorite={currentPlanIsFavorite}
            onToggleFavorite={currentPlanId ? handleToggleCurrentPlanFavorite : null}
            initialActiveTab={planActiveTab}
            onActiveTabChange={handlePlanActiveTabChange}
            onResumePlanning={handleResumePlanning}
          />
        )}

        {/* Spacer for mobile nav visibility */}

      </div>

      {inlineBillingHost && (
        <EventInputScreen
          billingOverlayOnly
          inlineBillingRequest={inlineBillingHost}
          initialEventData={inlineBillingHost.eventData}
          existingThemes={themes.length > 0 ? themes : null}
          selectedTheme={selectedTheme}
          shoppingItems={shoppingItems}
          themePlansForReturn={themePlans}
          currentPlanId={currentPlanId}
          isAuthenticated={isAuthenticated}
          user={user}
          onBillingOverlayClose={() => {
            const host = inlineBillingHost;
            setInlineBillingHost(null);
            if (host.returnThemeKey) {
              setEventData((prev) => ({
                ...(prev || {}),
                __focusThemeKey: host.returnThemeKey,
                __returnThemeKey: host.returnThemeKey
              }));
              setThemeCarouselFocusKey(host.returnThemeKey);
            }
            if (host.returnToThemesOnClose || host.screen === 'carousel') {
              setCurrentScreen('carousel');
            } else if (host.screen === 'plan') {
              setCurrentScreen('plan');
            }
          }}
          onSubmit={() => {}}
        />
      )}

      {/* Modals placed relative to the layout context or global */}
      <ThemeSelectionModal
        isOpen={showDirectPlanModal}
        onClose={() => setShowDirectPlanModal(false)}
        onConfirm={handleDirectPlanConfirm}
        eventData={eventData}
        sidebarCollapsed={sidebarCollapsed}
      />

      <SavePlanModal
        isOpen={showSaveModal}
        onClose={() => {
          // Part A: Stay/Cancel — only close the modal and cancel the pending
          // navigation/discard intent. Do NOT clear pendingDraftDataRef or
          // pendingDraftData: those hold the captured form snapshot that
          // exitWizardToView captured before opening the modal. Clearing them
          // here would make the next Dashboard click lose dirty state and
          // navigate without showing the guard again.
          setShowSaveModal(false);
          setPendingDiscardAction(null);
          setPendingPostSaveDestination(null);
        }}
        planData={{
          event_data: stripTransientEventDataKeys({
            ...(activeDraftData || {}),
            plan_name: activeDraftData?.plan_name || eventData?.plan_name || pendingDraftData?.plan_name || ''
          }),
          themes,
          selected_theme: selectedTheme,
          shopping_items: shoppingItems,
          theme_plans: buildThemePlansForSave()
        }}
        currentPlanId={currentPlanId}
        // draftMode = true means: save in place, don't redirect to dashboard
        draftMode={
          (!pendingDiscardAction && !pendingPostSaveDestination && (!!pendingDraftDataRef.current || !!pendingDraftData)) ||
          (!pendingDiscardAction && !pendingPostSaveDestination && currentScreen === 'input') ||
          !!pendingPostSaveDestination
        }
        isNavigationExit={!!pendingPostSaveDestination}
        isNewUnsavedPlan={!currentPlanId}
        saveButtonLabel={pendingPostSaveDestination ? 'Save & Go' : undefined}
        discardButtonLabel={currentPlanId ? 'Discard Changes' : 'Discard Event'}
        onSave={(savedPlanId) => {
          const isDraftSave =
            !pendingDiscardAction &&
            !pendingPostSaveDestination &&
            (pendingDraftDataRef.current || pendingDraftData || currentScreen === 'input');
          if (isDraftSave) {
            if (savedPlanId) setCurrentPlanId(savedPlanId);
            if (currentScreen === 'input' && eventInputScreenRef.current) {
              setEventData(eventInputScreenRef.current.getCurrentFormData());
            }
            clearUnsavedChanges();
            setSaveSuccessToken(prev => prev + 1);
            setCanSave(false);
            planContentDirtyRef.current = false;
            pendingDraftDataRef.current = null;
            setPendingDraftData(null);
            setShowSaveModal(false);
          } else {
            // Full save (from nav or discard prompt): go to requested exit
            setShowSaveModal(false);
            pendingDraftDataRef.current = null;
            setPendingDraftData(null);
            setPendingDiscardAction(null);
            if (pendingPostSaveDestination) {
              const destination = pendingPostSaveDestination;
              setPendingPostSaveDestination(null);
              navigateAfterWizardExit(destination, savedPlanId);
            } else {
              resetAll();
              setShowLanding(true);
              if (savedPlanId) setHighlightPlanId(savedPlanId);
              window.__reloadDashboard = true;
              setCurrentView('dashboard');
            }
          }
        }}
        onDiscard={pendingDiscardAction ? () => {
          pendingDiscardAction();
          setPendingDiscardAction(null);
          setShowSaveModal(false);
        } : undefined}
        onRequestAuth={(name) => {
          setShowSaveModal(false);
          setAuthMode('signup');
          setPendingAction('save');
          setPendingSaveData({ active: true, name: name });
          setShowAuthModal(true);
        }}
        sidebarCollapsed={sidebarCollapsed}
      />

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        planId={currentPlanId}
        sidebarCollapsed={sidebarCollapsed}
      />

      <ThemeEditModal
        isOpen={themeEditModal.isOpen}
        theme={themeEditModal.theme}
        onClose={() => setThemeEditModal({ isOpen: false, theme: null })}
        onSave={handleSaveThemeEdit}
        eventData={eventData}
      />

      {renderGlobalConfirmationModal()}

      {showAuthModal && (
        <AuthModal
          mode={authMode}
          isSessionExpired={isSessionExpired}
          onClose={() => {
            setShowAuthModal(false);
            setIsSessionExpired(false);
          }}
          onSwitchMode={setAuthMode}
          onSuccess={(user) => {
            handleAuthSuccess(user);
            setIsSessionExpired(false);
            setSessionExpiredLanding(false);
          }}
          sidebarOffset={showLanding ? 0 : (sidebarCollapsed ? 64 : 256)}
        />
      )}

      {/* Demo Drawer */}
      {showDemoMenu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDemoMenu(false)}></div>
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl p-4">
            <button onClick={() => setShowDemoMenu(false)}>Close</button>
            {/* Menu items */}
          </div>
        </div>
      )}

      {showSessionEndedModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl text-center border border-gray-100">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Session Ended</h3>
            <p className="text-gray-600 mb-8 text-sm sm:text-base leading-relaxed">
              You were signed out in another tab. Unsaved changes in this tab may not be saved.
            </p>
            <button
              onClick={returnToPublicHomeAfterSessionEnd}
              className="w-full py-3.5 px-6 font-bold text-white rounded-full bg-purple-600 hover:bg-purple-700 active:bg-purple-800 transition duration-150 shadow-lg hover:shadow-xl focus:outline-none"
            >
              Return to Home
            </button>
          </div>
        </div>
      )}

    </AppLayout>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
